/* eslint-disable no-undef */
/**
 * Optimized geojson edit interactions for Cesium.
 * GPU-bounded snapping around mouse, across ALL dataSources.
 * Lag fixes: adaptive sampling, mouse delta gate, rebuild cadence, per-frame budget.
 * Notes: comments in English by user request.
 */

import * as Cesium from "cesium";
import { cloneEntity, safeGetById, showToast } from "../Utility";
import { arcDrawingActive } from "./CreateEntityOnQuadraticBezier";
import { toggleDirectionalArrows } from "../geojson/Arrow";
import Type from "../Types";
import { computeZoomLevel, disableCameraControls, restoreCameraControls } from "../MapUtility";
import { createEdgeAutoPan } from "./EdgeAutoPan";

export const enableGeojsonEditInteractions = (viewer, dataSource, originalCameraControls, pushUndo, selectedEntitiesRef, moItem, snappingOptions, editTypeRef, directionArrowSettingRef, settings) => {
  // -------------------- Perf knobs --------------------
  const PERF = {
    CP_VIEWPAD_PX: 40,
    NEAR_HIGHLIGHT_RADIUS_PX: 15,
    MOUSE_THROTTLE_MS: 8,
    MOUSE_MIN_DELTA_PX: 2, // skip rebuild if mouse moved less than this
    MAX_CP_RENDER: Number.POSITIVE_INFINITY,
    CP_WINDOW: 28,
    CP_DECIMATE_MINSTEP: 6,

    // GPU-limited snap sampling
    SNAP_GPU_RADIUS_PX: 50,
    SNAP_GPU_SAMPLE_STEP_PX: 8, // fewer samples
    SNAP_GPU_MAX_SAMPLES: 128, // tighter cap
    SNAP_GPU_DRILL_LIMIT: 6, // fewer drill hits per pixel
    SNAP_GPU_MAX_ENTITIES: 120, // unique entities cap
    SNAP_REBUILD_COOLDOWN_MS: 60, // rebuild cadence while dragging
    SNAP_REBUILD_BUDGET_MS: 4.0, // hard time budget per rebuild
    SNAP_TOTAL_POINT_CAP: Number.POSITIVE_INFINITY, // total points kept near mouse
    SNAP_PREVIEW_STICKY_RADIUS_PX: 70,
    // preview radius
    SNAP_PREVIEW_RADIUS_PX: 70,

    // legacy near-scan/highlight
    NEAR_SCAN_COOLDOWN_MS: 100,
    MAX_NEAR_SCAN_ENTITIES: 200,
    INSERT_HOVER_THRESHOLD_PX: 24,
    INSERT_STICKY_LEEWAY_PX: 34,
    AUTOPAN_EDGE_PX: 60,
    SHIFT_LINK_RADIUS_PX: 40,
    ZOOM_MIN: 14,
    ZOOM_MIN_POLYGON: 14,

    SNAP_INDEX_CELL_DEG: 0.0001,
    SNAP_INDEX_QUERY_RADIUS_CELLS: 2,
  };

  const SNAP_ZOOM_CONFIG = [
    { maxZoom: 18, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 1 },
    { maxZoom: 19, gpuRadiusPx: 15, previewStickyRadiusPx: 17, tol: 2 },
    { maxZoom: 20, gpuRadiusPx: 30, previewStickyRadiusPx: 35, tol: 3 },
    { maxZoom: 21, gpuRadiusPx: 35, previewStickyRadiusPx: 40, tol: 4 },
    { maxZoom: 22, gpuRadiusPx: 40, previewStickyRadiusPx: 45, tol: 5 },
    { maxZoom: 23, gpuRadiusPx: 45, previewStickyRadiusPx: 50, tol: 6 },
    { maxZoom: 24, gpuRadiusPx: 50, previewStickyRadiusPx: 55, tol: 7 },
    { maxZoom: 25, gpuRadiusPx: 60, previewStickyRadiusPx: 65, tol: 8 },
    { maxZoom: 35, gpuRadiusPx: 70, previewStickyRadiusPx: 75, tol: 8 },
  ];

  function getSnapZoomConfig() {
    const zoom = typeof computeZoomLevel === "function" ? computeZoomLevel(viewer) : 0;
    for (const cfg of SNAP_ZOOM_CONFIG) {
      if (zoom <= cfg.maxZoom) return cfg;
    }
    return SNAP_ZOOM_CONFIG[SNAP_ZOOM_CONFIG.length - 1];
  }

  // ---------------- Overlay DS (always draws last) ----------------
  const OVERLAY_DS_NAME = "__editOverlay";
  let overlayDs = viewer.dataSources.getByName?.(OVERLAY_DS_NAME)?.[0];
  if (!overlayDs) {
    overlayDs = new Cesium.CustomDataSource(OVERLAY_DS_NAME);
    viewer.dataSources.add(overlayDs);
  }

  let entityOwnerDs = new WeakMap(); // entity -> owner dataSource

  function readPropAtTime(prop, time) {
    return typeof prop?.getValue === "function" ? prop.getValue(time) : prop;
  }

  function getOwnerDataSource(entity) {
    if (!entity) return null;
    const cached = entityOwnerDs.get(entity);
    if (cached) return cached;
    const coll = viewer?.dataSources;
    if (!coll) return null;
    for (let i = 0; i < coll.length; i++) {
      const ds = coll.get(i);
      if (!ds || ds === overlayDs) continue;
      try {
        if (ds.entities?.contains?.(entity)) {
          entityOwnerDs.set(entity, ds);
          return ds;
        }
      } catch {}
    }
    return null;
  }

  function isEntityGeometryShown(entity) {
    // short English comment: honor per-graphics show flags (polyline/polygon/point)
    const t = viewer?.clock?.currentTime || Cesium.JulianDate.now();
    if (entity?.polyline) {
      const s = entity.polyline.show;
      if (s != null && readPropAtTime(s, t) === false) return false;
    } else if (entity?.polygon) {
      const s = entity.polygon.show;
      if (s != null && readPropAtTime(s, t) === false) return false;
    } else if (entity?.point) {
      const s = entity.point.show;
      if (s != null && readPropAtTime(s, t) === false) return false;
    }
    return true;
  }

  function isEntityInteractive(entity, ownerDs = null) {
    // short English comment: skip entities that are not currently rendered/visible
    if (!entity) return false;
    if (isHelperEntity(entity)) return false;

    const ds = ownerDs || getOwnerDataSource(entity);
    if (ds && ds.show === false) return false;

    try {
      if ("isShowing" in entity && entity.isShowing === false) return false;
    } catch {}
    if (entity.show === false) return false;

    if (!isEntityGeometryShown(entity)) return false;
    return true;
  }

  const isViewerAlive = () => viewer && !viewer.isDestroyed?.() && viewer.scene && !viewer.scene.isDestroyed?.();

  function ensureOverlayIsLast() {
    const coll = viewer?.dataSources;
    if (!coll || !overlayDs) return;
    try {
      const last = coll.get(coll.length - 1);
      if (last !== overlayDs) coll.raiseToTop(overlayDs);
    } catch {}
  }

  const getZoom = () => (typeof computeZoomLevel === "function" ? computeZoomLevel(viewer) : 0);

  const canShowDetail = () => getZoom() >= PERF.ZOOM_MIN;

  const canShowCpForEntity = (entity) => {
    if (!isEntityInteractive(entity)) return false;
    const min = entity?.polygon ? PERF.ZOOM_MIN_POLYGON : PERF.ZOOM_MIN;
    return getZoom() >= min;
  };

  // ---------------- Zoom-reactive pixel size ----------------
  function zoomPixelSize(viewer, base, opts = {}) {
    const near = opts.near ?? 200;
    const far = opts.far ?? 2_000_000;
    const minF = opts.minFactor ?? 0.85;
    const maxF = opts.maxFactor ?? 2.2;
    const lnNear = Math.log(near);
    const lnFar = Math.log(far);
    return new Cesium.CallbackProperty(() => {
      const h = viewer?.scene?.camera?.positionCartographic?.height || near;
      const t = Cesium.Math.clamp((Math.log(h) - lnNear) / (lnFar - lnNear), 0, 1);
      const factor = minF + (maxF - minF) * t;
      return base * factor;
    }, false);
  }

  // ---------------- Snap tolerance (fixed per zoom, in px) ----------------
  function getDynamicSnapTolerancePx(basePx) {
    const cfg = getSnapZoomConfig();

    // short English comment: choose tolerance per zoom and kind
    if (basePx === SNAP_VERTEX_TOLERANCE_BASE) return cfg.tol;
    if (basePx === SNAP_ENTITYMID_TOLERANCE_BASE) return cfg.tol;

    // fallback for any other caller
    return basePx;
  }

  // short English comment: scalar based on camera height (same curve as zoomPixelSize)
  function zoomScalar(viewer, opts = {}) {
    const near = opts.near ?? 1;
    const far = opts.far ?? 1800;
    const minF = opts.minFactor ?? 0.85;
    const maxF = opts.maxFactor ?? 2.2;
    const lnNear = Math.log(near);
    const lnFar = Math.log(far);
    const h = viewer?.scene?.camera?.positionCartographic?.height || near;
    const t = Cesium.Math.clamp((Math.log(h) - lnNear) / (lnFar - lnNear), 0, 1);

    return minF + (maxF - minF) * t;
  }

  // dynamic snap radii in pixels (now fixed per zoom level)
  function getSnapGpuRadiusPx() {
    const cfg = getSnapZoomConfig();
    // short English comment: fixed GPU snap radius per zoom
    return cfg.gpuRadiusPx;
  }

  function getSnapPreviewStickyRadiusPx() {
    const cfg = getSnapZoomConfig();
    // short English comment: fixed preview radius per zoom
    return cfg.previewStickyRadiusPx;
  }

  // ---------------- Shared state ----------------
  let shiftMultiDrag = false;
  let multiDragKey = null;
  let linkedVertices = null;

  // -----------------------
  // Shift: nearest-line + nearby-vertices (3m) selection + arrow nudge (2cm)
  // -----------------------
  let shiftNudgeActive = false;
  let shiftNudgeEntityId = null;
  let shiftNudgeIndices = [];
  let shiftNudgeCps = []; // overlay entities (NOT normal controlPointsMap)
  let shiftNudgePostRenderAttached = false;
  let lastShiftNudgeRebuildTs = 0;
  let shiftNudgePumpRaf = 0;
  let shiftNudgePumpUntil = 0;

  function stopShiftNudgePump() {
    if (shiftNudgePumpRaf) cancelAnimationFrame(shiftNudgePumpRaf);
    shiftNudgePumpRaf = 0;
    shiftNudgePumpUntil = 0;
  }

  function startShiftNudgePump(ms = 800) {
    stopShiftNudgePump();
    const now0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
    shiftNudgePumpUntil = now0 + ms;

    const tick = () => {
      shiftNudgePumpRaf = 0;
      if (!shiftNudgeActive) return;

      rebuildShiftNudgeSelection();

      const now = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
      const st = findNearestPolylineEntityByScreen._scanState;
      const stillScanning = !!st && st.idx !== 0;
      const noBestYet = !st?.bestEnt;
      const noEntityYet = !shiftNudgeEntityId;

      if (now < shiftNudgePumpUntil && (noEntityYet || (stillScanning && noBestYet))) {
        shiftNudgePumpRaf = requestAnimationFrame(tick);
      }
    };

    shiftNudgePumpRaf = requestAnimationFrame(tick);
  }

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

  const controlPointsMap = new Map();
  const originalWidthMap = new Map();

  let keydownHandler = null;
  let highlightedCp = null;

  let draggingPoint = null;
  let draggingEntities = null;
  let dragStartCartesian = null;
  let insertPointEntity = null;
  let hoveredEntity = null;
  let hoveredSegment = null;
  let endpointExtendMode = null;

  let controlPointDragOriginal = null;
  let _origInertia = null;
  let helpersSuppressed = false;

  let shiftHeld = false;
  let spaceHeld = false;
  function finishDrag(reason = "") {
    if (pendingBulkChanges && pendingBulkChanges.length) {
      pushUndo({ type: "bulk-modify", changes: pendingBulkChanges });
    }
    pendingBulkChanges = null;

    draggingPoint = null;
    draggingEntities = null;
    dragStartCartesian = null;
    controlPointDragOriginal = null;
    helpersSuppressed = false;

    snapCache = null;
    lastSnapBuildTs = 0;
    lastMouseForSnap = null;
    snapExcludeEntityId = null;
    snapExcludeIndex = null;

    shiftMultiDrag = false;
    linkedVertices = null;
    multiDragKey = null;

    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();
    clearDragMeasureOverlay();

    dragMeasureMode = "mid";
    dragMeasureCpEntity = null;

    restoreCameraControls(viewer, originalCameraControls);
    stopAutoPan();

    if (moItem?.arrow_visibility) {
      toggleDirectionalArrows(viewer, dataSource, true, directionArrowSettingRef);
    }
    try {
      viewer.__intersectionPointsRefresh?.();
    } catch {}

    viewer.scene.requestRender?.();
  }

  let pendingBulkChanges = null;

  function findDataSourceOfEntity(ent) {
    const coll = viewer?.dataSources;
    if (!coll) return dataSource;
    for (let i = 0; i < coll.length; i++) {
      const ds = coll.get(i);
      try {
        if (ds?.entities?.getById(ent.id)) return ds;
      } catch {}
    }
    return dataSource;
  }

  // Backspace state
  let focusedControlPointInfo = { entityId: null, index: null };
  let backspaceTimer = null;
  let backspaceActive = false;

  // Endpoint "+" helpers
  let activePlusCp = null;
  let lastDownOnControlPoint = null;

  // ---------------- Snapping (GPU-limited) ----------------
  let SNAP_SYSTEM_ENABLED = true;

  const SNAP_VERTEX_ENABLED = !!snappingOptions?.vertex;
  const SNAP_VERTEX_TOLERANCE_BASE = 17; // base px at medium zoom
  const SNAP_ENTITYMID_ENABLED = !!snappingOptions?.midpoint;
  const SNAP_ENTITYMID_TOLERANCE_BASE = 17; // base px at medium zoom

  let snapCache = null; // { vertices:[{ent,index,p,p2d,dist2}], entityMids:[{ent,p,p2d,dist2}] }
  let lastSnapBuildTs = 0;
  let lastMouseForSnap = null; // {x,y}
  let snapExcludeEntityId = null;
  let snapExcludeIndex = null;

  // Map<"ix,iy", Array<{ ent, index }>>
  let snapIndex = new Map();
  let snapIndexReady = false;

  function snapIndexCellKeyFromDegrees(lonDeg, latDeg) {
    const s = PERF.SNAP_INDEX_CELL_DEG;
    const ix = Math.floor(lonDeg / s);
    const iy = Math.floor(latDeg / s);
    return ix + "," + iy;
  }

  function snapIndexReset() {
    snapIndex = new Map();
    snapIndexReady = false;
  }

  function snapIndexMoveVertex(ent, vertexIndex, oldPos, newPos) {
    // short English comment: move single vertex record between cells
    if (!snapIndexReady || !ent || vertexIndex == null) return;

    const ellipsoid = viewer.scene?.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;

    const oldCarto = Cesium.Cartographic.fromCartesian(oldPos, ellipsoid);
    const newCarto = Cesium.Cartographic.fromCartesian(newPos, ellipsoid);
    if (!oldCarto || !newCarto) return;

    const oldKey = snapIndexCellKeyFromDegrees(Cesium.Math.toDegrees(oldCarto.longitude), Cesium.Math.toDegrees(oldCarto.latitude));
    const newKey = snapIndexCellKeyFromDegrees(Cesium.Math.toDegrees(newCarto.longitude), Cesium.Math.toDegrees(newCarto.latitude));

    if (oldKey === newKey) return; // still same cell, nothing to do

    const bucketOld = snapIndex.get(oldKey);
    if (bucketOld && bucketOld.length) {
      for (let i = 0; i < bucketOld.length; i++) {
        const rec = bucketOld[i];
        if (rec.ent === ent && rec.index === vertexIndex) {
          bucketOld.splice(i, 1);
          break;
        }
      }
      if (!bucketOld.length) snapIndex.delete(oldKey);
    }

    let bucketNew = snapIndex.get(newKey);
    if (!bucketNew) {
      bucketNew = [];
      snapIndex.set(newKey, bucketNew);
    }
    bucketNew.push({ ent, index: vertexIndex });
  }

  const MIN_SEG_LEN_M = 0.1;
  const INSERT_HOVER_THRESHOLD_PX = PERF.INSERT_HOVER_THRESHOLD_PX;
  const INSERT_STICKY_LEEWAY_PX = PERF.INSERT_STICKY_LEEWAY_PX;

  let lastInsertInfo = { entityId: null, seg: null, pos: null, visible: false };

  // --------------- Utils ---------------
  function clearCpHighlight() {
    if (highlightedCp?.point) highlightedCp.point.outlineWidth = 0;
    highlightedCp = null;
  }
  // FIX: map global vertex index -> cp index via indices array
  function highlightCp(entityId, index) {
    clearCpHighlight();
    const obj = controlPointsMap.get(entityId);
    if (!obj) return;

    // find the control point index in the virtualized list
    const k = obj.indices ? obj.indices.indexOf(index) : -1;
    if (k < 0 || !obj.cps?.[k]?.point) return;

    const cp = obj.cps[k];
    cp.point.outlineColor = Cesium.Color.WHITE;
    cp.point.outlineWidth = 3; // show highlight ring
    highlightedCp = cp;
  }

  function isDrawingHelper(entity) {
    const read = (p) => (typeof p?.getValue === "function" ? p.getValue() : p);
    const props = entity?.properties;
    if (!props) return false;
    return !!(read(props.isDrawingPreview) || read(props.isDrawingTemp));
  }

  function preserveZ(originalPoint, updatedCartesian) {
    const carto = Cesium.Cartographic.fromCartesian(updatedCartesian);
    const originalCarto = Cesium.Cartographic.fromCartesian(originalPoint);
    return Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, originalCarto.height);
  }

  // helper: compute height rule for an entity
  function computeEntityHeightRule(entity) {
    // short English comment: compute height: 0 if majority zeros, else avg of non-zero
    const positions = getPositionsFromEntity(entity) || [];
    if (!positions.length) return 0;
    let zeroCount = 0;
    let nonZeroSum = 0;
    let nonZeroCount = 0;
    for (const p of positions) {
      const h = Cesium.Cartographic.fromCartesian(p).height || 0;
      if (Math.abs(h) < 1e-3) zeroCount++;
      else {
        nonZeroSum += h;
        nonZeroCount++;
      }
    }
    if (zeroCount > positions.length / 2) return 0;
    if (nonZeroCount === 0) return 0;
    return nonZeroSum / nonZeroCount;
  }

  // helper: set lon/lat of cartesian but override height per entity rule
  function setHeightFromEntity(cartesian, entity) {
    // short English comment: keep lon/lat, set height per computeEntityHeightRule
    const c = Cesium.Cartographic.fromCartesian(cartesian);
    const h = computeEntityHeightRule(entity);
    return Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, h);
  }

  function getPositionsFromEntity(entity) {
    if (entity?.polyline) {
      return entity.polyline.positions.getValue();
    } else if (entity?.polygon) {
      const raw = entity.polygon.hierarchy.getValue().positions;
      const positions = [...raw];
      if (positions.length > 2 && Cesium.Cartesian3.equals(positions[0], positions[positions.length - 1])) positions.pop();
      return positions;
    } else if (entity?.position) {
      return [entity.position.getValue()];
    }
    return [];
  }

  function snapIndexAddEntityPositions(ent, positions, ellipsoid, ownerDs) {
    // Add all vertices of an entity to index
    if (!positions || !positions.length) return;
    for (let i = 0; i < positions.length; i++) {
      const carto = Cesium.Cartographic.fromCartesian(positions[i], ellipsoid);
      if (!carto) continue;
      const lonDeg = Cesium.Math.toDegrees(carto.longitude);
      const latDeg = Cesium.Math.toDegrees(carto.latitude);
      const key = snapIndexCellKeyFromDegrees(lonDeg, latDeg);
      let bucket = snapIndex.get(key);
      if (!bucket) {
        bucket = [];
        snapIndex.set(key, bucket);
      }
      bucket.push({ ent, index: i, ownerDs });
    }
  }

  function buildGlobalSnapIndex() {
    // Build spatial index across all dataSources
    snapIndexReset();
    entityOwnerDs = new WeakMap(); // entity -> owner dataSource

    if (!SNAP_SYSTEM_ENABLED) return;

    const dsCollection = viewer?.dataSources;
    if (!dsCollection) return;
    const ellipsoid = viewer.scene?.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;

    for (let i = 0; i < dsCollection.length; i++) {
      const ds = dsCollection.get(i);
      if (!ds || ds === overlayDs) continue;
      const entities = ds.entities?.values || [];
      if (!entities.length) continue;
      for (let j = 0; j < entities.length; j++) {
        const ent = entities[j];
        if (!ent) continue;
        entityOwnerDs.set(ent, ds);
        if (isHelperEntity(ent)) continue; // skip overlay helpers
        const positions = getPositionsFromEntity(ent);
        if (!positions?.length) continue;
        snapIndexAddEntityPositions(ent, positions, ellipsoid, ds);
      }
    }

    snapIndexReady = true;
  }

  buildGlobalSnapIndex();

  function setPositionsToEntity(entity, positionsRef) {
    if (entity?.polyline) {
      entity.polyline.positions = new Cesium.CallbackProperty(() => positionsRef, false);
    } else if (entity?.polygon) {
      if (!Cesium.Cartesian3.equals(positionsRef[0], positionsRef[positionsRef.length - 1])) positionsRef.push(positionsRef[0]);
      entity.polygon.hierarchy = new Cesium.CallbackProperty(() => new Cesium.PolygonHierarchy(positionsRef), false);
    } else if (entity?.position) {
      entity.position = new Cesium.CallbackProperty(() => positionsRef[0], false);
    }
  }

  function toWin(cart) {
    const v = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, cart) : viewer.scene.cartesianToCanvasCoordinates(cart);
    return v ? { x: v.x, y: v.y } : null;
  }
  function distPxBetweenCarts(a, b) {
    // screen-space distance between two world-space points
    const a2 = toWin(a);
    const b2 = toWin(b);
    if (!a2 || !b2) return Infinity;
    return Math.hypot(a2.x - b2.x, a2.y - b2.y);
  }

  function mouseDistPx(winMouse, cart) {
    const p = toWin(cart);
    if (!winMouse || !p) return Infinity;
    return Math.hypot(winMouse.x - p.x, winMouse.y - p.y);
  }

  function findNearestSegmentPositionPx(positions, winMouse, thresholdPx = INSERT_HOVER_THRESHOLD_PX) {
    if (!positions || positions.length < 2 || !winMouse) return null;
    let best = null;
    for (let i = 0; i < positions.length - 1; i++) {
      const p1 = positions[i];
      const p2 = positions[i + 1];
      if (Cesium.Cartesian3.distance(p1, p2) < MIN_SEG_LEN_M) continue;
      const aC = Cesium.Cartographic.fromCartesian(p1);
      const bC = Cesium.Cartographic.fromCartesian(p2);
      const midLon = Cesium.Math.lerp(aC.longitude, bC.longitude, 0.5);
      const midLat = Cesium.Math.lerp(aC.latitude, bC.latitude, 0.5);
      const midH = Cesium.Math.lerp(aC.height, bC.height, 0.5);
      const midWorld = Cesium.Cartesian3.fromRadians(midLon, midLat, midH);
      const mid2d = toWin(midWorld);
      if (!mid2d) continue;
      const dpx = Math.hypot(winMouse.x - mid2d.x, winMouse.y - mid2d.y);
      if (dpx <= thresholdPx) {
        if (!best || dpx < best.distPx) best = { position: midWorld, index: i, distPx: dpx };
      }
    }
    return best;
  }

  // Add this helper
  function distToSegmentPx(a, b, mouseWin) {
    // nearest distance from mouse to segment AB in screen space
    const a2 = toWin(a);
    const b2 = toWin(b);
    if (!a2 || !b2 || !mouseWin) return Infinity;

    const vx = b2.x - a2.x;
    const vy = b2.y - a2.y;
    const wx = mouseWin.x - a2.x;
    const wy = mouseWin.y - a2.y;
    const vv = vx * vx + vy * vy;
    if (!vv) return Math.hypot(mouseWin.x - a2.x, mouseWin.y - a2.y);

    let t = (wx * vx + wy * vy) / vv;
    t = Cesium.Math.clamp(t, 0, 1);

    const cx = a2.x + t * vx;
    const cy = a2.y + t * vy;
    return Math.hypot(mouseWin.x - cx, mouseWin.y - cy);
  }

  // Return param t in [0,1] and pixel distance from mouse to closest point on AB in screen-space
  function projectTOnSegment2D(a, b, mouseWin) {
    const a2 = toWin(a);
    const b2 = toWin(b);
    if (!a2 || !b2 || !mouseWin) return { t: 0.5, dPx: Infinity };
    const vx = b2.x - a2.x,
      vy = b2.y - a2.y;
    const wx = mouseWin.x - a2.x,
      wy = mouseWin.y - a2.y;
    const vv = vx * vx + vy * vy;
    if (!vv) return { t: 0, dPx: Math.hypot(wx, wy) };
    let t = (wx * vx + wy * vy) / vv;
    t = Cesium.Math.clamp(t, 0, 1);
    const cx = a2.x + t * vx,
      cy = a2.y + t * vy;
    const dPx = Math.hypot(mouseWin.x - cx, mouseWin.y - cy);
    return { t, dPx };
  }

  // Interpolate lon/lat/height between two Cartesian3 by param t
  function interpolateCartographic(a, b, t) {
    const ac = Cesium.Cartographic.fromCartesian(a);
    const bc = Cesium.Cartographic.fromCartesian(b);
    const lon = Cesium.Math.lerp(ac.longitude, bc.longitude, t);
    const lat = Cesium.Math.lerp(ac.latitude, bc.latitude, t);
    const h = Cesium.Math.lerp(ac.height, bc.height, t);
    return Cesium.Cartesian3.fromRadians(lon, lat, h);
  }

  // Replace the whole function
  function isMouseNearEntityPx(ent, mouse, px = 10) {
    if (!mouse || !(ent?.polyline || ent?.polygon)) return false;

    const positions = getPositionsFromEntity(ent);
    if (!positions || positions.length < 2) return false;

    // cheap guard for very dense polygons
    if (ent.polygon && positions.length > 2000) return false;

    // scan all consecutive segments
    for (let i = 0; i < positions.length - 1; i++) {
      const p1 = positions[i],
        p2 = positions[i + 1];
      if (Cesium.Cartesian3.distance(p1, p2) < MIN_SEG_LEN_M) continue;
      if (distToSegmentPx(p1, p2, mouse) <= px) return true;
    }

    // include closing edge for polygons (last -> first), but only for proximity highlight
    if (ent.polygon && positions.length >= 3) {
      const pLast = positions[positions.length - 1];
      const pFirst = positions[0];
      if (Cesium.Cartesian3.distance(pLast, pFirst) >= MIN_SEG_LEN_M) {
        if (distToSegmentPx(pLast, pFirst, mouse) <= px) return true;
      }
    }

    return false;
  }

  // ----------------- Anchors -----------------
  function computePolylineHalfwayPoint(positions) {
    if (!positions || positions.length < 2) return null;
    let total = 0;
    const segLen = [];
    for (let i = 0; i < positions.length - 1; i++) {
      const d = Cesium.Cartesian3.distance(positions[i], positions[i + 1]);
      segLen.push(d);
      total += d;
    }
    if (total === 0) return positions[0];
    const target = total / 2;
    let acc = 0;
    for (let i = 0; i < segLen.length; i++) {
      const d = segLen[i];
      if (acc + d >= target) {
        const t = (target - acc) / (d || 1);
        const aC = Cesium.Cartographic.fromCartesian(positions[i]);
        const bC = Cesium.Cartographic.fromCartesian(positions[i + 1]);
        const lon = Cesium.Math.lerp(aC.longitude, bC.longitude, t);
        const lat = Cesium.Math.lerp(aC.latitude, bC.latitude, t);
        const h = Cesium.Math.lerp(aC.height, bC.height, t);
        return Cesium.Cartesian3.fromRadians(lon, lat, h);
      }
      acc += d;
    }
    return positions[positions.length - 1];
  }
  function computePolygonCenter(positions) {
    if (!positions?.length) return null;
    const bs = Cesium.BoundingSphere.fromPoints(positions);
    const center = bs.center;
    const cCarto = Cesium.Cartographic.fromCartesian(center);
    let sumH = 0;
    for (const p of positions) sumH += Cesium.Cartographic.fromCartesian(p).height;
    const avgH = sumH / positions.length;
    return Cesium.Cartesian3.fromRadians(cCarto.longitude, cCarto.latitude, avgH);
  }
  function getEntityAnchor(entity) {
    if (entity?.polyline) return computePolylineHalfwayPoint(entity.polyline.positions.getValue());
    if (entity?.polygon) return computePolygonCenter(getPositionsFromEntity(entity));
    if (entity?.position) return entity.position.getValue();
    return null;
  }
  function averageAnchors(anchors) {
    if (!anchors?.length) return null;
    const sum = anchors.reduce(
      (acc, c) => {
        acc.x += c.x;
        acc.y += c.y;
        acc.z += c.z;
        return acc;
      },
      new Cesium.Cartesian3(0, 0, 0),
    );
    return new Cesium.Cartesian3(sum.x / anchors.length, sum.y / anchors.length, sum.z / anchors.length);
  }

  // ---------------- GPU-limited snap: helpers ----------------
  function isHelperEntity(ent) {
    const p = ent?.properties;
    return (
      p?.isControlPoint?.getValue?.() === true ||
      p?.isInsertPoint?.getValue?.() === true ||
      p?.isMidpointSnapIndicator?.getValue?.() === true ||
      p?.isSnapPreviewVertex?.getValue?.() === true ||
      p?.isSnapPreviewMid?.getValue?.() === true ||
      p?.isDrawingPreview?.getValue?.() === true ||
      p?.isDrawingTemp?.getValue?.() === true ||
      p?.isArcPreview?.getValue?.() === true ||
      p?.isDragMeasureLine?.getValue?.() === true ||
      p?.isDragMeasureLabel?.getValue?.() === true
    );
  }

  function sampleOffsetsInDisk(radius, step, maxSamples) {
    // grid sampling inside a circle, ordered by distance
    const arr = [];
    for (let dy = -radius; dy <= radius; dy += step) {
      for (let dx = -radius; dx <= radius; dx += step) {
        const d2 = dx * dx + dy * dy;
        if (d2 <= radius * radius) arr.push({ dx, dy, d2 });
      }
    }
    arr.sort((a, b) => a.d2 - b.d2);
    return arr.slice(0, maxSamples);
  }
  const _diskOffsets = sampleOffsetsInDisk(PERF.SNAP_GPU_RADIUS_PX, PERF.SNAP_GPU_SAMPLE_STEP_PX, PERF.SNAP_GPU_MAX_SAMPLES);

  function gpuPickNearbyEntities(mouseWin) {
    // Collect across ALL dataSources. Exclude overlay/helpers only.
    const seen = new Set();
    const list = [];
    if (!mouseWin) return list;
    const limit = PERF.SNAP_GPU_DRILL_LIMIT;

    // time budget guard
    const t0 = performance.now();

    for (let i = 0; i < _diskOffsets.length; i++) {
      const o = _diskOffsets[i];
      const wp = new Cesium.Cartesian2(mouseWin.x + o.dx, mouseWin.y + o.dy);
      const results = viewer.scene.drillPick(wp, limit) || [];
      for (const r of results) {
        const ent = r?.id;
        if (!ent) continue;
        if (isHelperEntity(ent)) continue;
        // do not restrict to a specific dataSource; include all
        if (seen.has(ent.id)) continue;
        seen.add(ent.id);
        list.push(ent);
        if (list.length >= PERF.SNAP_GPU_MAX_ENTITIES) return list;
      }
      if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) break;
    }
    return list;
  }

  function buildSnapCacheGPU(mouseWin, excludeEntityId, excludeIndex) {
    // Reimplemented to use spatial index instead of GPU drillPick
    if (!SNAP_SYSTEM_ENABLED) return null;
    if (!snapIndexReady || !mouseWin) return { vertices: [], entityMids: [] };

    const radiusPx = getSnapGpuRadiusPx(); // dynamic by zoom
    const r2 = radiusPx * radiusPx;

    const mouseCartesian = pickCartesian(mouseWin);
    if (!mouseCartesian) return { vertices: [], entityMids: [] };

    const ellipsoid = viewer.scene?.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;
    const carto = Cesium.Cartographic.fromCartesian(mouseCartesian, ellipsoid);
    if (!carto) return { vertices: [], entityMids: [] };

    const lonDeg = Cesium.Math.toDegrees(carto.longitude);
    const latDeg = Cesium.Math.toDegrees(carto.latitude);
    const cellSize = PERF.SNAP_INDEX_CELL_DEG;
    const cx = Math.floor(lonDeg / cellSize);
    const cy = Math.floor(latDeg / cellSize);

    const verts = [];
    const mids = [];
    const perEntity = new Map(); // id -> { ent, indices:number[] }

    const t0 = performance.now();

    const pushVert = (obj) => {
      // keep array sorted ascending by dist2, capped
      let lo = 0;
      let hi = verts.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (obj.dist2 < verts[mid].dist2) hi = mid;
        else lo = mid + 1;
      }
      verts.splice(lo, 0, obj);
      if (verts.length > PERF.SNAP_TOTAL_POINT_CAP) verts.length = PERF.SNAP_TOTAL_POINT_CAP;
    };

    const pushMid = (obj) => {
      // push midpoint, do not sort now
      mids.push(obj);
    };

    // Collect candidate vertices from neighbor cells
    let overBudget = false;
    for (let ix = cx - PERF.SNAP_INDEX_QUERY_RADIUS_CELLS; ix <= cx + PERF.SNAP_INDEX_QUERY_RADIUS_CELLS && !overBudget; ix++) {
      for (let iy = cy - PERF.SNAP_INDEX_QUERY_RADIUS_CELLS; iy <= cy + PERF.SNAP_INDEX_QUERY_RADIUS_CELLS; iy++) {
        if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) {
          overBudget = true;
          break;
        }

        const key = ix + "," + iy;
        const bucket = snapIndex.get(key);
        if (!bucket || !bucket.length) continue;

        for (let n = 0; n < bucket.length; n++) {
          const rec = bucket[n];
          const ent = rec.ent;
          if (!ent) continue;

          const id = ent.id || ent._id || ent;
          let group = perEntity.get(id);
          if (!group) {
            group = { ent, ownerDs: rec.ownerDs || null, indices: [] };

            perEntity.set(id, group);
          }
          group.indices.push(rec.index);
          if (!group.ownerDs && rec.ownerDs) group.ownerDs = rec.ownerDs;

          if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) {
            overBudget = true;
            break;
          }
        }
      }
    }

    // For each entity, compute positions and filter by pixel distance
    for (const group of perEntity.values()) {
      if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) break;
      const ent = group.ent;
      if (!isEntityInteractive(ent, group.ownerDs)) continue;
      const positions = getPositionsFromEntity(ent);
      if (!positions?.length) continue;

      for (let k = 0; k < group.indices.length; k++) {
        const idx = group.indices[k];
        if (idx < 0 || idx >= positions.length) continue;

        if (excludeEntityId && ent.id === excludeEntityId && idx === excludeIndex) continue;

        const p = positions[idx];
        const p2d = toWin(p);
        if (!p2d) continue;

        const dx = p2d.x - mouseWin.x;
        const dy = p2d.y - mouseWin.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r2) {
          pushVert({ ent, index: idx, p, p2d, dist2: d2 });
        }

        if (verts.length >= PERF.SNAP_TOTAL_POINT_CAP) break;
        if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) break;
      }
    }

    // Optional: one midpoint per nearby entity
    if (SNAP_ENTITYMID_ENABLED && verts.length < PERF.SNAP_TOTAL_POINT_CAP) {
      for (const group of perEntity.values()) {
        if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) break;

        const ent = group.ent;
        if (!isEntityInteractive(ent, group.ownerDs)) continue; // short English comment: skip hidden mids

        const positions = getPositionsFromEntity(ent);
        if (!positions?.length) continue;

        let mid = null;
        if (ent.polyline && positions.length >= 2) {
          mid = computePolylineHalfwayPoint(positions);
        } else if (ent.polygon && positions.length >= 3) {
          mid = computePolygonCenter(positions);
        } else if (ent.position) {
          mid = positions[0];
        }

        if (!mid) continue;

        const p2d = toWin(mid);
        if (!p2d) continue;

        const dx = p2d.x - mouseWin.x;
        const dy = p2d.y - mouseWin.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r2) {
          pushMid({ ent, ownerDs: group.ownerDs || null, p: mid, p2d, dist2: d2 });
        }

        if (mids.length >= PERF.SNAP_TOTAL_POINT_CAP) break;
      }
    }

    // Trim mids so total <= cap, prefer closest
    let remaining = Math.max(0, PERF.SNAP_TOTAL_POINT_CAP - verts.length);
    mids.sort((a, b) => a.dist2 - b.dist2);
    if (mids.length > remaining) mids.length = remaining;

    return { vertices: verts, entityMids: mids };
  }

  function findVertexSnap(movement) {
    if (!SNAP_SYSTEM_ENABLED || !SNAP_VERTEX_ENABLED || !snapCache?.vertices?.length) return null;
    const mouse = movement.endPosition || movement.position;
    if (!mouse) return null;

    const tolPx = getDynamicSnapTolerancePx(SNAP_VERTEX_TOLERANCE_BASE);
    const tol2 = tolPx * tolPx;

    for (const v of snapCache.vertices) {
      if (v.dist2 <= tol2) {
        return { dist: Math.sqrt(v.dist2), cartesian: v.p, vertex: v };
      }
    }
    return null;
  }

  function findEntityMidSnap(movement) {
    if (!SNAP_SYSTEM_ENABLED || !SNAP_ENTITYMID_ENABLED || !snapCache?.entityMids?.length) return null;
    const mouse = movement.endPosition || movement.position;
    if (!mouse) return null;

    const tolPx = getDynamicSnapTolerancePx(SNAP_ENTITYMID_TOLERANCE_BASE);
    const tol2 = tolPx * tolPx;

    for (const m of snapCache.entityMids) {
      if (m.dist2 <= tol2) {
        return { dist: Math.sqrt(m.dist2), cartesian: m.p, mid: m };
      }
    }
    return null;
  }

  // ---------------- Drag measure overlay (singletons) ----------------
  let dragMeasureStart = null,
    dragMeasureCurrent = null;
  let dragMeasureLine = null,
    dragMeasureLabel = null;
  let dragMeasureMode = "mid"; // "mid" | "cp"
  let dragMeasureCpEntity = null; // follow this cp when dragging

  function ensureDragMeasureOverlay() {
    if (!dragMeasureLine) {
      dragMeasureLine = overlayDs.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => (dragMeasureStart && dragMeasureCurrent ? [dragMeasureStart, dragMeasureCurrent] : []), false),
          width: 2.0,
          material: new Cesium.PolylineDashMaterialProperty({ color: Cesium.Color.YELLOW, dashLength: 16.0 }),
          clampToGround: false,
        },
        properties: { isDragMeasureLine: true },
        show: false,
      });
    }
    if (!dragMeasureLabel) {
      dragMeasureLabel = overlayDs.entities.add({
        position: new Cesium.CallbackProperty(() => {
          if (!dragMeasureStart || !dragMeasureCurrent) return undefined;

          // when dragging a CP, stick the label on the CP
          if (dragMeasureMode === "cp" && dragMeasureCpEntity) {
            const pos = dragMeasureCpEntity.position;
            return typeof pos?.getValue === "function" ? pos.getValue() : pos;
          }

          // default: middle of dashed line
          return Cesium.Cartesian3.midpoint(dragMeasureStart, dragMeasureCurrent, new Cesium.Cartesian3());
        }, false),

        label: {
          text: new Cesium.CallbackProperty(() => {
            if (!dragMeasureStart || !dragMeasureCurrent) return "";
            const d = Cesium.Cartesian3.distance(dragMeasureStart, dragMeasureCurrent);
            return `${d.toFixed(2)} m`;
          }, false),
          font: "bold 14px sans-serif",
          fillColor: Cesium.Color.WHITE,
          showBackground: true,
          backgroundColor: Cesium.Color.BLACK.withAlpha(0.6),
          pixelOffset: new Cesium.Cartesian2(0, -10),
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: { isDragMeasureLabel: true },
        show: false,
      });
    }
  }
  function initMeasureForControlPoint(cpEntity) {
    const parentEntityId = readProp(cpEntity.properties.parentEntityId);
    const parentEntity = dataSource.entities.getById(parentEntityId);
    const index = readProp(cpEntity.properties.pointIndex);
    const obj = controlPointsMap.get(parentEntity.id);
    if (!obj) return;
    dragMeasureStart = Cesium.Cartesian3.clone(obj.positionsRef[index], new Cesium.Cartesian3());
    dragMeasureCurrent = Cesium.Cartesian3.clone(dragMeasureStart, new Cesium.Cartesian3());
    dragMeasureMode = "cp"; // label follows cp
    dragMeasureCpEntity = cpEntity;
    ensureDragMeasureOverlay();
    dragMeasureLine.show = true;
    dragMeasureLabel.show = true;
  }
  function initMeasureForEntities(entities, startCartesian) {
    // short English comment: prefer drag start, fallback to anchors
    let start = startCartesian || null;

    if (!start) {
      const anchors = [];
      entities.forEach((ent) => {
        const a = getEntityAnchor(ent);
        if (a) anchors.push(a);
      });
      start = anchors.length === 1 ? anchors[0] : averageAnchors(anchors);
    }

    if (!start) return;

    dragMeasureStart = Cesium.Cartesian3.clone(start, new Cesium.Cartesian3());
    dragMeasureCurrent = Cesium.Cartesian3.clone(start, new Cesium.Cartesian3());
    dragMeasureMode = "mid"; // back to middle of line
    dragMeasureCpEntity = null;
    ensureDragMeasureOverlay();
    dragMeasureLine.show = true;
    dragMeasureLabel.show = true;
  }

  function updateMeasureCurrent(pos) {
    if (dragMeasureStart) {
      dragMeasureCurrent = pos;
      viewer.scene.requestRender?.();
    }
  }
  function incrementMeasureCurrentBy(offset) {
    if (dragMeasureCurrent) {
      dragMeasureCurrent = Cesium.Cartesian3.add(dragMeasureCurrent, offset, new Cesium.Cartesian3());
      viewer.scene.requestRender?.();
    }
  }
  function clearDragMeasureOverlay() {
    if (dragMeasureLine) dragMeasureLine.show = false;
    if (dragMeasureLabel) dragMeasureLabel.show = false;
    dragMeasureStart = null;
    dragMeasureCurrent = null;
  }
  function updateEndpointExtendPreview(mouseWin) {
    if (!endpointExtendMode) return;

    const parentEntity = safeGetById(dataSource.entities, endpointExtendMode.parentEntityId);
    if (!parentEntity?.polyline) {
      endpointExtendMode = null;
      clearDragMeasureOverlay();
      return;
    }

    const positions = getPositionsFromEntity(parentEntity);
    if (!positions?.length) {
      clearDragMeasureOverlay();
      return;
    }

    const start = endpointExtendMode.atHead ? positions[0] : positions[positions.length - 1];

    const raw = pickCartesian(mouseWin);
    if (!raw) {
      clearDragMeasureOverlay();
      return;
    }

    const end = setHeightFromEntity(raw, parentEntity);

    // Use existing dashed overlay, label at midpoint, distance in meters
    dragMeasureStart = Cesium.Cartesian3.clone(start, new Cesium.Cartesian3());
    dragMeasureMode = "mid";
    dragMeasureCpEntity = null;

    ensureDragMeasureOverlay();
    dragMeasureLine.show = true;
    dragMeasureLabel.show = true;

    updateMeasureCurrent(end);
  }

  // ---------------- Snap preview markers (pool) ----------------

  let snapPreviewAnchorWorld = null; // world-space anchor of current snap cluster

  const snapPreviewPool = { vertex: [], mid: [] };
  function createVertexPreviewMarker() {
    const ent = overlayDs.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#00e5ff").withAlpha(0.95),
        pixelSize: 10,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewVertex: true }),
    });
    snapPreviewPool.vertex.push(ent);
    return ent;
  }
  function createMidpointPreviewMarker() {
    const ent = overlayDs.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      point: {
        color: Cesium.Color.fromCssColorString("#ff4dd2").withAlpha(0.95),
        pixelSize: 12,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
        outlineWidth: 1.5,
      },
      show: false,
      properties: new Cesium.PropertyBag({ isSnapPreviewMid: true }),
    });
    snapPreviewPool.mid.push(ent);
    return ent;
  }
  function hideAllSnapPreviewMarkers() {
    // short English comment: hide all preview markers and clear anchor
    for (const e of snapPreviewPool.vertex) e.show = false;
    for (const e of snapPreviewPool.mid) e.show = false;
    snapPreviewAnchorWorld = null;
  }

  // ---------------- Midpoint snap indicator ----------------
  let midpointSnapMarker = null;
  function showMidpointSnapIndicator(position) {
    if (!SNAP_SYSTEM_ENABLED) return;
    ensureOverlayIsLast();
    if (!midpointSnapMarker) {
      midpointSnapMarker = overlayDs.entities.add({
        position,
        point: {
          color: Cesium.Color.fromCssColorString("#ff69b4").withAlpha(0.95),
          pixelSize: 12,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 1.5,
        },
        properties: new Cesium.PropertyBag({ isMidpointSnapIndicator: true }),
      });
    } else {
      midpointSnapMarker.position = position;
      midpointSnapMarker.show = true;
    }
  }
  function hideMidpointSnapIndicator() {
    if (!SNAP_SYSTEM_ENABLED) return;
    if (midpointSnapMarker) midpointSnapMarker.show = false;
  }

  // Build a 7-decimal lat/lon key in degrees. Ignore height.
  function latLonKeyDeg(cart) {
    const c = Cesium.Cartographic.fromCartesian(cart);
    return `${Cesium.Math.toDegrees(c.latitude).toFixed(7)},${Cesium.Math.toDegrees(c.longitude).toFixed(7)}`;
  }

  // Check if a window point is inside current canvas
  function isPointInViewport(win) {
    if (!win) return false;
    const w = viewer.canvas.clientWidth,
      h = viewer.canvas.clientHeight;
    return win.x >= 0 && win.y >= 0 && win.x < w && win.y < h;
  }

  // Cheap visibility: any vertex projects inside viewport
  function entityVisibleInViewport(ent) {
    const positions = getPositionsFromEntity(ent);
    for (let i = 0; i < positions.length; i++) {
      const p2 = toWin(positions[i]);
      if (p2 && isPointInViewport(p2)) return true;
    }
    return false;
  }

  // pick entities near a window point within a pixel radius
  function gpuPickNearbyEntitiesWithin(mouseWin, radiusPx) {
    const offsets = sampleOffsetsInDisk(radiusPx, PERF.SNAP_GPU_SAMPLE_STEP_PX, PERF.SNAP_GPU_MAX_SAMPLES);
    const seen = new Set(),
      list = [];
    const limit = PERF.SNAP_GPU_DRILL_LIMIT;
    const t0 = performance.now();
    for (let i = 0; i < offsets.length; i++) {
      const o = offsets[i];
      const wp = new Cesium.Cartesian2(mouseWin.x + o.dx, mouseWin.y + o.dy);
      const res = viewer.scene.drillPick(wp, limit) || [];
      for (const r of res) {
        const ent = r?.id;
        if (!ent || isHelperEntity(ent) || seen.has(ent.id)) continue;
        seen.add(ent.id);
        list.push(ent);
        if (list.length >= PERF.SNAP_GPU_MAX_ENTITIES) return list;
      }
      if (performance.now() - t0 >= PERF.SNAP_REBUILD_BUDGET_MS) break;
    }
    return list;
  }

  // Collect all vertices (across ALL dataSources) whose lat/lon equals the key (7 d.p.), excluding helpers.
  // Includes parent entity too for duplicate endpoints, but skips the primary index itself.
  // Collect all vertices whose lat/lon equals the key, but only within a pixel radius.
  // only scan entities near the seed within radiusPx
  function buildLinkedVerticesForKey(key, parentEntity, primaryIndex, seedCartesian, radiusPx = 100) {
    const out = [];
    const seedWin = toWin(seedCartesian);
    const candidates = seedWin ? gpuPickNearbyEntitiesWithin(seedWin, radiusPx) : [];
    for (const ent of candidates) {
      const positions = getPositionsFromEntity(ent);
      if (!positions?.length) continue;

      const indices = [],
        originals = [];
      for (let k = 0; k < positions.length; k++) {
        if (ent === parentEntity && k === primaryIndex) continue;
        if (latLonKeyDeg(positions[k]) !== key) continue;
        if (distPxBetweenCarts(positions[k], seedCartesian) > radiusPx) continue; // keep pixel radius
        indices.push(k);
        originals.push(Cesium.Cartesian3.clone(positions[k], new Cesium.Cartesian3()));
      }
      if (indices.length) out.push({ entity: ent, indices, positionsRef: positions, originals });
    }
    return out;
  }

  // Apply same ECEF delta to all linked vertices while preserving each vertex's own height
  // Apply same lon/lat delta (radians). Keep each vertex height (ignore z equality).
  function applyCartoDeltaToLinkedVertices(seedBefore, seedAfter) {
    if (!linkedVertices?.length || !seedBefore || !seedAfter) return;
    const cb = Cesium.Cartographic.fromCartesian(seedBefore);
    const ca = Cesium.Cartographic.fromCartesian(seedAfter);
    const dLon = ca.longitude - cb.longitude;
    const dLat = ca.latitude - cb.latitude;

    for (const item of linkedVertices) {
      const { entity, positionsRef, indices, originals } = item;
      for (let i = 0; i < indices.length; i++) {
        const oc = Cesium.Cartographic.fromCartesian(originals[i]);
        const nLon = oc.longitude + dLon;
        const nLat = oc.latitude + dLat;
        positionsRef[indices[i]] = Cesium.Cartesian3.fromRadians(nLon, nLat, oc.height); // keep z
      }
      setPositionsToEntity(entity, positionsRef);
    }
  }

  // ---------------- Insert preview (yellow) ----------------
  function showInsertPoint(position) {
    if (helpersSuppressed) return;
    ensureOverlayIsLast();
    if (!insertPointEntity) {
      insertPointEntity = overlayDs.entities.add({
        position,
        point: {
          color: Cesium.Color.YELLOW.withAlpha(1),
          pixelSize: zoomPixelSize(viewer, 7, { near: 150, far: 1_500_000, minFactor: 2.0, maxFactor: 0.85 }),
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: new Cesium.PropertyBag({ isInsertPoint: true }),
      });
    } else {
      insertPointEntity.position = position;
      insertPointEntity.show = true;
    }
  }
  function hideInsertPoint() {
    if (insertPointEntity) insertPointEntity.show = false;
    hoveredSegment = null;
    hoveredEntity = null;
    lastInsertInfo = { entityId: null, seg: null, pos: null, visible: false };
  }

  // ---------------- Control point creation (virtualized) ----------------
  function createControlPoint(position, index, parentEntity) {
    return overlayDs.entities.add({
      position,
      point: {
        color: Cesium.Color.RED,
        pixelSize: zoomPixelSize(viewer, 7, { near: 150, far: 1_500_000, minFactor: 2.2, maxFactor: 0.85 }),
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineWidth: 3,
        outlineColor: Cesium.Color.WHITE,
      },
      label: {
        text: "+",
        show: false,
        font: "bold 20px sans-serif",
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineColor: Cesium.Color.BLACK.withAlpha(0.95),
        outlineWidth: 3,
        fillColor: Cesium.Color.WHITE,
        showBackground: true,
        backgroundColor: Cesium.Color.BLACK.withAlpha(0.55),
        pixelOffset: new Cesium.Cartesian2(0, -18),
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        scaleByDistance: new Cesium.NearFarScalar(150.0, 1.35, 1_500_000.0, 0.9),
        translucencyByDistance: new Cesium.NearFarScalar(150.0, 1.0, 1_500_000.0, 1.0),
      },
      properties: new Cesium.PropertyBag({
        isControlPoint: true,
        parentEntityId: parentEntity?.id ?? null,
        pointIndex: index,
      }),
    });
  }

  function removeControlPointsForEntity(entity) {
    const obj = controlPointsMap.get(entity.id);
    if (!obj) return;
    obj.cps.forEach((cp) => overlayDs.entities.remove(cp));
    controlPointsMap.delete(entity.id);
    if (focusedControlPointInfo.entityId === entity.id) {
      focusedControlPointInfo = { entityId: null, index: null };
      clearCpHighlight();
    }
  }
  function removeAllControlPoints() {
    for (const id of Array.from(controlPointsMap.keys())) {
      const e = safeGetById(dataSource.entities, id);
      if (e) removeControlPointsForEntity(e);
    }
    focusedControlPointInfo = { entityId: null, index: null };
    clearCpHighlight();
  }

  function computeVirtualCpIndices(positions, hotspotIdx) {
    const n = positions.length;
    if (n <= PERF.MAX_CP_RENDER) return { indices: Array.from({ length: n }, (_, i) => i), mode: "all", baseIndex: 0 };
    const win = PERF.CP_WINDOW;
    const lo = Math.max(0, hotspotIdx - win);
    const hi = Math.min(n - 1, hotspotIdx + win);
    let arr = [];
    for (let i = lo; i <= hi; i++) arr.push(i);
    const stride = Math.max(PERF.CP_DECIMATE_MINSTEP, Math.ceil(n / (PERF.MAX_CP_RENDER - arr.length + 1)));
    for (let i = 0; i < n; i += stride) {
      if (i < lo || i > hi) arr.push(i);
    }
    arr = Array.from(new Set(arr)).sort((a, b) => a - b);
    if (arr.length > PERF.MAX_CP_RENDER) arr = arr.filter((_, k) => k % Math.ceil(arr.length / PERF.MAX_CP_RENDER) === 0);
    return { indices: arr, mode: "virtual", baseIndex: arr[0] };
  }

  function isPointInViewportPad(win, pad = 0) {
    if (!win) return false;
    const w = viewer.canvas.clientWidth,
      h = viewer.canvas.clientHeight;
    return win.x >= -pad && win.y >= -pad && win.x < w + pad && win.y < h + pad;
  }
  function shouldShowCp(positionsRef, idx) {
    const p2 = toWin(positionsRef[idx]);
    return p2 && isPointInViewportPad(p2, PERF.CP_VIEWPAD_PX);
  }

  function rebuildControlPointsForEntity(entity, hotspotIdx = null) {
    const positionsRef = getPositionsFromEntity(entity);
    if (!positionsRef?.length) return;

    let hs =
      hotspotIdx != null ? hotspotIdx : focusedControlPointInfo.entityId === entity.id && focusedControlPointInfo.index != null ? focusedControlPointInfo.index : Math.floor(positionsRef.length / 2);

    const { indices, mode } = computeVirtualCpIndices(positionsRef, hs);

    let vis = indices.filter((i) => shouldShowCp(positionsRef, i));

    if (!vis.length && indices.length) {
      const center = new Cesium.Cartesian2(viewer.canvas.clientWidth * 0.5, viewer.canvas.clientHeight * 0.5);
      vis = indices
        .map((i) => ({ i, p: toWin(positionsRef[i]) }))
        .filter((o) => !!o.p)
        .sort((a, b) => Math.hypot(a.p.x - center.x, a.p.y - center.y) - Math.hypot(b.p.x - center.x, b.p.y - center.y))
        .slice(0, 1)
        .map((o) => o.i);
    }

    removeControlPointsForEntity(entity);
    ensureOverlayIsLast();

    const cps = vis.map((idx) => createControlPoint(positionsRef[idx], idx, entity));
    controlPointsMap.set(entity.id, { cps, positionsRef, mode, indices: vis, baseIndex: vis[0] ?? 0 });
    setPositionsToEntity(entity, positionsRef);
  }
  function showControlPointsForEntity(entity, hotspotIdx = null) {
    if (helpersSuppressed) return;
    if (!entity || !canShowCpForEntity(entity)) {
      if (entity?.id && controlPointsMap.has(entity.id)) removeControlPointsForEntity(entity);
      return;
    }
    const positionsRef = getPositionsFromEntity(entity);
    if (!positionsRef?.length) return;
    if (!controlPointsMap.has(entity.id)) {
      rebuildControlPointsForEntity(entity, hotspotIdx);
      return;
    }
    const obj = controlPointsMap.get(entity.id);
    if (obj.mode === "virtual" && hotspotIdx != null) {
      const minIdx = obj.indices[0];
      const maxIdx = obj.indices[obj.indices.length - 1];
      if (hotspotIdx < minIdx + 4 || hotspotIdx > maxIdx - 4) rebuildControlPointsForEntity(entity, hotspotIdx);
    }
  }

  function showPlusOnControlPoint(cpEntity) {
    if (!cpEntity?.label) return;
    if (activePlusCp && activePlusCp !== cpEntity && activePlusCp.label) activePlusCp.label.show = false;
    cpEntity.label.show = true;
    activePlusCp = cpEntity;
  }
  function hidePlusIndicator() {
    if (activePlusCp?.label) activePlusCp.label.show = false;
    activePlusCp = null;
  }
  function isEndpointControlPoint(cpEntity) {
    if (!cpEntity?.properties?.isControlPoint?.getValue?.()) return false;
    const parentEntityId = readProp(cpEntity.properties.parentEntityId);
    const parentEntity = safeGetById(dataSource.entities, parentEntityId);
    if (!parentEntity?.polyline) return false;
    const vertexIndex = readProp(cpEntity.properties.pointIndex);
    const positions = getPositionsFromEntity(parentEntity);
    if (!positions?.length) return false;
    const lastIdx = positions.length - 1;
    return vertexIndex === 0 || vertexIndex === lastIdx;
  }
  function insertEndpointDuplicate(parentEntity, atHead) {
    if (!parentEntity?.polyline) return;
    const positions = getPositionsFromEntity(parentEntity);
    if (!positions?.length) return;
    const clone = Cesium.Cartesian3.clone(atHead ? positions[0] : positions[positions.length - 1], new Cesium.Cartesian3());
    if (atHead) positions.splice(0, 0, clone);
    else positions.push(clone);
    setPositionsToEntity(parentEntity, positions);
    rebuildControlPointsForEntity(parentEntity, atHead ? 0 : positions.length - 1);
    focusedControlPointInfo = { entityId: parentEntity.id, index: atHead ? 0 : positions.length - 1 };
    hideInsertPoint();
    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();
    viewer.scene.requestRender?.();
  }

  function handleEndpointExtendClick(click) {
    if (!endpointExtendMode) return;

    const parentEntity = safeGetById(dataSource.entities, endpointExtendMode.parentEntityId);
    if (!parentEntity?.polyline) {
      endpointExtendMode = null;
      clearDragMeasureOverlay();
      return;
    }

    const rawPos = pickCartesian(click.position);
    if (!rawPos) return;

    // short English comment: keep lon/lat, set height from entity rule
    const newPos = setHeightFromEntity(rawPos, parentEntity);
    const positions = getPositionsFromEntity(parentEntity);
    if (!positions?.length) return;

    // short English comment: capture undo once for the whole extend session
    if (!endpointExtendMode.undoCaptured && editTypeRef.current != "new" && !arcDrawingActive) {
      const origW = originalWidthMap.get(parentEntity.id);
      if (origW != null && parentEntity.polyline) {
        parentEntity.polyline.width = origW;
      }
      const snapshot = cloneEntity(parentEntity);
      pushUndo({ type: "modify", entitySnapshot: snapshot, entityId: parentEntity.id });
      endpointExtendMode.undoCaptured = true;
    }

    if (endpointExtendMode.atHead) {
      positions.splice(0, 0, newPos);
    } else {
      positions.push(newPos);
    }

    const newIndex = endpointExtendMode.atHead ? 0 : positions.length - 1;

    setPositionsToEntity(parentEntity, positions);
    rebuildControlPointsForEntity(parentEntity, newIndex);
    focusedControlPointInfo = { entityId: parentEntity.id, index: newIndex };

    showControlPointsForEntity(parentEntity, newIndex);
    highlightCp(parentEntity.id, newIndex);
    showEntityHighlightWidth(parentEntity);
    viewer.scene.requestRender?.();
    updateEndpointExtendPreview(lastMouseWin);
  }

  function listEntitiesInDataSource(ds) {
    const v = ds?.entities?.values || [];
    return v.filter((e) => !isHelperEntity(e));
  }

  // ----------------- AUTOPAN -----------------
  const AUTOPAN_EDGE_PX = PERF.AUTOPAN_EDGE_PX;
  const edgeAutoPan = createEdgeAutoPan({
    viewer,
    edgePx: AUTOPAN_EDGE_PX,

    // same profile you tested
    speedMin: 5,
    speedMax: 60,
    speedZGt24: 5,
    speedZ22_24: 20,
    speedZLt22: 50,
  });

  let lastMouseWin = null;
  let lastMouseClient = null; // {x,y} in client coords
  const RAW_MOUSE_OPTS = { passive: true, capture: true };

  function refreshMouseAnchorFromLastClient() {
    if (!lastMouseClient) return;
    const rect = viewer.canvas.getBoundingClientRect();
    const x = lastMouseClient.x - rect.left;
    const y = lastMouseClient.y - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    lastMouseWin = new Cesium.Cartesian2(x, y);
  }

  function onRawMouseMove(ev) {
    lastMouseClient = { x: ev.clientX, y: ev.clientY };
    refreshMouseAnchorFromLastClient();
  }

  // capture mouse even when cursor is over UI (not the canvas)
  window.addEventListener("mousemove", onRawMouseMove, RAW_MOUSE_OPTS);
  window.addEventListener("pointermove", onRawMouseMove, RAW_MOUSE_OPTS);
  window.addEventListener("mousedown", onRawMouseMove, RAW_MOUSE_OPTS);
  window.addEventListener("pointerdown", onRawMouseMove, RAW_MOUSE_OPTS);
  window.addEventListener("wheel", onRawMouseMove, RAW_MOUSE_OPTS);

  let autoPanActive = false;
  let autoPanVec = { x: 0, y: 0, magX: 0, magY: 0 };
  let lastAutoPanTime = null;

  function pickCartesian(windowPos) {
    if (!windowPos) return undefined;

    const { scene } = viewer;
    let result;

    if (scene.pickPositionSupported) {
      try {
        result = scene.pickPosition(windowPos);
      } catch {
        result = undefined;
      }
    }

    if (!Cesium.defined(result)) {
      result = scene.camera.pickEllipsoid(windowPos, scene.globe.ellipsoid);
    }

    return Cesium.defined(result) ? result : undefined;
  }

  function groundDistanceMeters(a, b) {
    if (!a || !b) return Infinity;
    const ca = Cesium.Cartographic.fromCartesian(a);
    const cb = Cesium.Cartographic.fromCartesian(b);

    // Ignore height completely
    const ga = new Cesium.Cartographic(ca.longitude, ca.latitude, 0);
    const gb = new Cesium.Cartographic(cb.longitude, cb.latitude, 0);

    const g = new Cesium.EllipsoidGeodesic(ga, gb, viewer.scene.globe.ellipsoid);
    return g.surfaceDistance;
  }

  function getWindowAnchorPos() {
    refreshMouseAnchorFromLastClient();
    // Mouse usually stays fixed; if we don't have lastMouseWin, use screen center
    if (lastMouseWin) return lastMouseWin;
    return new Cesium.Cartesian2(viewer.canvas.clientWidth * 0.5, viewer.canvas.clientHeight * 0.5);
  }

  function clearShiftNudgeSelection() {
    if (shiftNudgeCps?.length) {
      for (const cp of shiftNudgeCps) overlayDs.entities.remove(cp);
    }
    shiftNudgeCps = [];
    shiftNudgeEntityId = null;
    shiftNudgeIndices = [];
  }

  function createShiftNudgePoint(position, index, parentEntity) {
    return overlayDs.entities.add({
      position,
      point: {
        color: Cesium.Color.CYAN,
        pixelSize: zoomPixelSize(viewer, 8, { near: 150, far: 1_500_000, minFactor: 2.2, maxFactor: 0.85 }),
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineWidth: 3,
        outlineColor: Cesium.Color.BLACK.withAlpha(0.85),
      },
      properties: new Cesium.PropertyBag({
        isShiftNudgePoint: true,
        parentEntityId: parentEntity?.id ?? null,
        pointIndex: index,
      }),
    });
  }

  // Replace your current findNearestPolylineEntityByScreen with this version.
  // It finds the nearest polyline by true world-distance to the mouse point,
  // while staying fast by using drillPick sampling as a broad-phase.

  function findNearestPolylineEntityByScreen(windowPos) {
    if (!windowPos) return null;

    const scene = viewer.scene;
    const time = Cesium.JulianDate.now();

    const mouseWorld = pickCartesian(windowPos);
    if (!mouseWorld) return null;

    // --- local ENU for fast meter distances ---
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(mouseWorld);
    const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    const toENU2 = (cart) => {
      const p = Cesium.Matrix4.multiplyByPoint(invEnu, cart, new Cesium.Cartesian3());
      return { x: p.x, y: p.y };
    };

    const distPointToSeg2 = (p, a, b) => {
      const abx = b.x - a.x,
        aby = b.y - a.y;
      const apx = p.x - a.x,
        apy = p.y - a.y;
      const ab2 = abx * abx + aby * aby;
      if (ab2 === 0) {
        const dx = p.x - a.x,
          dy = p.y - a.y;
        return dx * dx + dy * dy;
      }
      let t = (apx * abx + apy * aby) / ab2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      const cx = a.x + abx * t,
        cy = a.y + aby * t;
      const dx = p.x - cx,
        dy = p.y - cy;
      return dx * dx + dy * dy;
    };

    const p0 = { x: 0, y: 0 };

    // --- time budget (prevents freeze) ---
    const start = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
    const BUDGET_MS = 6;
    const overBudget = () => {
      const now = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
      return now - start > BUDGET_MS;
    };

    // --- caches across calls (prevents "must be near the line") ---
    const FN = findNearestPolylineEntityByScreen;
    if (!FN._bsCache) FN._bsCache = new Map();
    if (!FN._scanState) FN._scanState = { idx: 0, bestEnt: null, bestD2: Infinity, lastKey: "" };
    const bsCache = FN._bsCache;
    const scanState = FN._scanState;

    const getCachedBS = (ent, positions) => {
      const key = ent.id || ent;
      const c = bsCache.get(key);
      if (c && c.posRef === positions) return c.bs;
      const bs = Cesium.BoundingSphere.fromPoints(positions);
      bsCache.set(key, { posRef: positions, bs });
      return bs;
    };

    const evalPolylineMinD2 = (ent, currentBestD2) => {
      if (!ent?.polyline) return Infinity;
      if (!canShowCpForEntity(ent)) return Infinity;

      const positions = Cesium.Property.getValueOrUndefined(ent.polyline.positions, time);
      if (!positions || positions.length < 2) return Infinity;

      const bs = getCachedBS(ent, positions);

      // prune with sphere (cheap)
      const centerDist = Cesium.Cartesian3.distance(mouseWorld, bs.center);
      const minPossible = Math.max(0, centerDist - bs.radius);
      const minPossible2 = minPossible * minPossible;
      if (minPossible2 >= currentBestD2) return Infinity;

      let bestD2 = currentBestD2;
      let prev = toENU2(positions[0]);

      for (let i = 1; i < positions.length; i++) {
        const cur = toENU2(positions[i]);
        const d2 = distPointToSeg2(p0, prev, cur);
        if (d2 < bestD2) bestD2 = d2;
        prev = cur;
        if (overBudget()) break;
      }

      return bestD2;
    };

    // ---------- 1) Broad-phase: bounded spiral drillPick (fast + not "must be near") ----------
    // Fixed number of picks => no freeze.
    const candidates = new Set();

    const MAX_R = 260; // pixels
    const STEP_R = 12; // pixels
    const ANGLES = 16; // per ring
    const MAX_PICKS = 220; // hard cap

    let picks = 0;

    // try direct pick first
    {
      const hit = scene.pick(windowPos);
      const ent = hit?.id;
      if (ent?.polyline && dataSource?.entities?.contains?.(ent) && canShowCpForEntity(ent)) {
        candidates.add(ent);
      }
    }

    for (let r = 0; r <= MAX_R; r += STEP_R) {
      const n = r === 0 ? 1 : ANGLES;
      for (let i = 0; i < n; i++) {
        const a = r === 0 ? 0 : (i * (Math.PI * 2)) / n;
        const s = new Cesium.Cartesian2(windowPos.x + Math.cos(a) * r, windowPos.y + Math.sin(a) * r);

        const picked = scene.drillPick(s);
        picks++;

        for (const hit of picked || []) {
          const ent = hit?.id;
          if (!ent?.polyline) continue;
          if (!dataSource?.entities?.contains?.(ent)) continue;
          if (!canShowCpForEntity(ent)) continue;
          candidates.add(ent);
        }

        if (picks >= MAX_PICKS || overBudget()) break;
      }
      if (picks >= MAX_PICKS || overBudget()) break;
    }

    // ---------- 2) Narrow-phase: true nearest among candidates ----------
    let bestEnt = null;
    let bestD2 = Infinity;

    if (candidates.size > 0) {
      for (const ent of candidates) {
        const d2 = evalPolylineMinD2(ent, bestD2);
        if (d2 < bestD2) {
          bestD2 = d2;
          bestEnt = ent;
        }
        if (overBudget()) break;
      }

      // remember last good result for stability
      scanState.bestEnt = bestEnt || scanState.bestEnt;
      scanState.bestD2 = bestEnt ? bestD2 : scanState.bestD2;

      return bestEnt || scanState.bestEnt || null;
    }

    // ---------- 3) Fallback: incremental full scan (never freezes, doesn’t require closeness) ----------
    const all = dataSource?.entities?.values || [];

    // reset scan if "mouse moved a lot" (keyed coarsely)
    const key = `${Math.round(windowPos.x / 20)}|${Math.round(windowPos.y / 20)}`;
    if (scanState.lastKey !== key) {
      scanState.lastKey = key;
      scanState.idx = 0;
      scanState.bestEnt = scanState.bestEnt || null;
      scanState.bestD2 = scanState.bestEnt ? scanState.bestD2 : Infinity;
    }

    let i = scanState.idx;
    const N = all.length;

    while (i < N && !overBudget()) {
      const ent = all[i];
      if (ent?.polyline && canShowCpForEntity(ent)) {
        const d2 = evalPolylineMinD2(ent, scanState.bestD2);
        if (d2 < scanState.bestD2) {
          scanState.bestD2 = d2;
          scanState.bestEnt = ent;
        }
      }
      i++;
    }

    scanState.idx = i >= N ? 0 : i;

    return scanState.bestEnt || null;
  }

  function arraysEqualInt(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  function rebuildShiftNudgeSelection() {
    if (!shiftNudgeActive) return;
    if (!isViewerAlive()) return;

    const win = getWindowAnchorPos();
    const mouseWorld = pickCartesian(win);
    if (!mouseWorld) {
      clearShiftNudgeSelection();
      return;
    }

    const nearest = findNearestPolylineEntityByScreen(win);
    if (!nearest) {
      clearShiftNudgeSelection();
      return;
    }

    const positions = getPositionsFromEntity(nearest);
    if (!positions?.length) {
      clearShiftNudgeSelection();
      return;
    }

    // Radius from settings (meters)
    const rRaw = Number(settings?.editing?.shiftSelectionRadius ?? 3);
    const radius = Number.isFinite(rRaw) ? Math.min(1000, Math.max(0.05, rRaw)) : 3;

    // Fast rough filter limit must depend on radius (keep it slightly larger)
    const roughLimit = radius + 1.0;

    // Collect vertex indices within radius (ground/surface distance, height ignored)
    const within = [];
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (!p) continue;

      // Fast rough filter (3D distance), then exact ground distance (ignore height)
      const rough = Cesium.Cartesian3.distance(mouseWorld, p);
      if (rough > roughLimit) continue;

      if (groundDistanceMeters(mouseWorld, p) <= radius) within.push(i);
    }

    if (!within.length) {
      // Keep nearest line but no points -> clear points
      clearShiftNudgeSelection();
      shiftNudgeEntityId = nearest.id;
      return;
    }

    // If same entity + same indices, just keep (and update positions if needed)
    const sameEntity = shiftNudgeEntityId === nearest.id;
    const sameIndices = arraysEqualInt(within, shiftNudgeIndices);

    if (sameEntity && sameIndices && shiftNudgeCps?.length) {
      // Update CP positions to match current entity positions (in case entity changed elsewhere)
      for (let j = 0; j < shiftNudgeIndices.length; j++) {
        const idx = shiftNudgeIndices[j];
        const cp = shiftNudgeCps[j];
        if (cp && positions[idx]) cp.position = positions[idx];
      }
      return;
    }

    // Otherwise rebuild
    clearShiftNudgeSelection();
    ensureOverlayIsLast();

    shiftNudgeEntityId = nearest.id;
    shiftNudgeIndices = within.slice();

    shiftNudgeCps = within.map((idx) => createShiftNudgePoint(positions[idx], idx, nearest));
    viewer.scene.requestRender?.();
  }

  // ---- Shift rebuild scheduling (camera.changed + debounce) ----
  let shiftNudgeCamAttached = false;
  let shiftNudgeCamTimer = null;

  function scheduleShiftNudgeRebuild(ms = 90) {
    if (!shiftNudgeActive) return;
    if (shiftNudgeCamTimer) clearTimeout(shiftNudgeCamTimer);
    shiftNudgeCamTimer = setTimeout(() => {
      shiftNudgeCamTimer = null;
      rebuildShiftNudgeSelection();
    }, ms);
  }

  function onShiftNudgeCameraChanged() {
    if (!shiftNudgeActive) return;
    scheduleShiftNudgeRebuild(60);
    startShiftNudgePump(500);
  }

  function attachShiftNudgeCameraChanged() {
    if (shiftNudgeCamAttached) return;
    shiftNudgeCamAttached = true;
    viewer.camera.changed.addEventListener(onShiftNudgeCameraChanged);
  }

  function detachShiftNudgeCameraChanged() {
    stopShiftNudgePump();
    if (!shiftNudgeCamAttached) return;
    shiftNudgeCamAttached = false;
    viewer.camera.changed.removeEventListener(onShiftNudgeCameraChanged);
    if (shiftNudgeCamTimer) {
      clearTimeout(shiftNudgeCamTimer);
      shiftNudgeCamTimer = null;
    }
  }

  function nudgeCartesianENU(p, dir, meters) {
    if (!p) return p;

    // ENU basis at point
    const m = Cesium.Transforms.eastNorthUpToFixedFrame(p, viewer.scene.globe.ellipsoid);
    const east = Cesium.Matrix4.getColumn(m, 0, new Cesium.Cartesian3());
    const north = Cesium.Matrix4.getColumn(m, 1, new Cesium.Cartesian3());

    let delta = new Cesium.Cartesian3(0, 0, 0);
    if (dir === "up") delta = Cesium.Cartesian3.multiplyByScalar(north, meters, delta);
    if (dir === "down") delta = Cesium.Cartesian3.multiplyByScalar(north, -meters, delta);
    if (dir === "right") delta = Cesium.Cartesian3.multiplyByScalar(east, meters, delta);
    if (dir === "left") delta = Cesium.Cartesian3.multiplyByScalar(east, -meters, delta);

    const moved = Cesium.Cartesian3.add(p, delta, new Cesium.Cartesian3());

    // Preserve original height exactly
    const c0 = Cesium.Cartographic.fromCartesian(p);
    const c1 = Cesium.Cartographic.fromCartesian(moved);
    return Cesium.Cartesian3.fromRadians(c1.longitude, c1.latitude, c0.height);
  }

  function applyShiftNudge(dir) {
    if (!shiftNudgeActive) return;
    if (!shiftNudgeEntityId || !shiftNudgeIndices?.length) return;

    const e = safeGetById(dataSource.entities, shiftNudgeEntityId);
    if (!e?.polyline) return;

    const positions = getPositionsFromEntity(e);
    if (!positions?.length) return;

    const step = 0.02; // 2 cm

    for (let j = 0; j < shiftNudgeIndices.length; j++) {
      const idx = shiftNudgeIndices[j];
      if (idx < 0 || idx >= positions.length) continue;
      positions[idx] = nudgeCartesianENU(positions[idx], dir, step);
    }

    setPositionsToEntity(e, positions);

    // Update CPs
    for (let j = 0; j < shiftNudgeIndices.length; j++) {
      const idx = shiftNudgeIndices[j];
      const cp = shiftNudgeCps[j];
      if (cp && positions[idx]) cp.position = positions[idx];
    }

    viewer.scene.requestRender?.();
  }

  function updateAutoPanVector(mouse) {
    if (!mouse) {
      autoPanVec = { x: 0, y: 0, magX: 0, magY: 0 };
      return;
    }
    const w = viewer.canvas.clientWidth,
      h = viewer.canvas.clientHeight;
    const dLeft = Math.max(0, mouse.x),
      dRight = Math.max(0, w - 1 - mouse.x),
      dTop = Math.max(0, mouse.y),
      dBottom = Math.max(0, h - 1 - mouse.y);
    const cLeft = mouse.x <= AUTOPAN_EDGE_PX ? (AUTOPAN_EDGE_PX - dLeft) / AUTOPAN_EDGE_PX : 0;
    const cRight = mouse.x >= w - AUTOPAN_EDGE_PX ? (AUTOPAN_EDGE_PX - dRight) / AUTOPAN_EDGE_PX : 0;
    const cTop = mouse.y <= AUTOPAN_EDGE_PX ? (AUTOPAN_EDGE_PX - dTop) / AUTOPAN_EDGE_PX : 0;
    const cBottom = mouse.y >= h - AUTOPAN_EDGE_PX ? (AUTOPAN_EDGE_PX - dBottom) / AUTOPAN_EDGE_PX : 0;
    const vx = cRight - cLeft;
    const vy = cTop - cBottom;
    const ease = (t) => t * t;
    const ex = Math.sign(vx) * ease(Math.abs(vx));
    const ey = Math.sign(vy) * ease(Math.abs(vy));
    autoPanVec = { x: ex, y: ey, magX: Math.abs(ex), magY: Math.abs(ey) };
  }

  function updateSnapPreviewMarkers(movement) {
    const mouseWin = movement?.endPosition || movement?.position || lastMouseWin;

    if (!SNAP_SYSTEM_ENABLED || helpersSuppressed) {
      hideAllSnapPreviewMarkers();
      return;
    }

    const stickyRadius = getSnapPreviewStickyRadiusPx();

    // short English comment: when not dragging or no cache, keep previews if mouse is still near anchor
    if (!draggingPoint || !snapCache) {
      if (snapPreviewAnchorWorld && mouseWin) {
        const anchor2d = toWin(snapPreviewAnchorWorld);
        if (anchor2d) {
          const dx = mouseWin.x - anchor2d.x;
          const dy = mouseWin.y - anchor2d.y;
          const d = Math.hypot(dx, dy);
          if (d <= stickyRadius) {
            return; // keep current markers visible
          }
        }
      }
      hideAllSnapPreviewMarkers();
      return;
    }

    const closeVertices = (snapCache.vertices || []).filter((v) => isEntityInteractive(v.ent));
    const closeMids = (snapCache.entityMids || []).filter((m) => isEntityInteractive(m.ent, m.ownerDs));

    const hasAny = closeVertices.length > 0 || closeMids.length > 0;

    // short English comment: if no new hits, use sticky radius around last anchor
    if (!hasAny) {
      if (snapPreviewAnchorWorld && mouseWin) {
        const anchor2d = toWin(snapPreviewAnchorWorld);
        if (anchor2d) {
          const dx = mouseWin.x - anchor2d.x;
          const dy = mouseWin.y - anchor2d.y;
          const d = Math.hypot(dx, dy);
          if (d <= stickyRadius) {
            return; // keep old markers
          }
        }
      }
      hideAllSnapPreviewMarkers();
      return;
    }

    while (snapPreviewPool.vertex.length < closeVertices.length) createVertexPreviewMarker();
    for (let i = 0; i < snapPreviewPool.vertex.length; i++) {
      const marker = snapPreviewPool.vertex[i];
      if (i < closeVertices.length) {
        marker.position = closeVertices[i].p;
        marker.show = true;
      } else {
        marker.show = false;
      }
    }

    while (snapPreviewPool.mid.length < closeMids.length) createMidpointPreviewMarker();
    for (let i = 0; i < snapPreviewPool.mid.length; i++) {
      const marker = snapPreviewPool.mid[i];
      if (i < closeMids.length) {
        marker.position = closeMids[i].p;
        marker.show = true;
      } else {
        marker.show = false;
      }
    }

    // short English comment: choose nearest snap as sticky anchor
    let best = null;
    if (closeVertices.length) best = closeVertices[0]; // vertices are sorted by dist2
    if (closeMids.length && (!best || closeMids[0].dist2 < best.dist2)) best = closeMids[0];
    snapPreviewAnchorWorld = best ? best.p : null;
  }

  const onAutoPanStep = (scene, time) => {
    if (!isViewerAlive()) {
      stopAutoPan();
      return;
    }
    if (!(draggingPoint || draggingEntities || endpointExtendMode)) {
      stopAutoPan();
      return;
    }
    if ((!autoPanVec.magX && !autoPanVec.magY) || !lastMouseWin) return;

    lastAutoPanTime = time;

    const w = viewer.canvas.clientWidth,
      h = viewer.canvas.clientHeight;
    const clamped = new Cesium.Cartesian2(Cesium.Math.clamp(lastMouseWin.x, 0, w - 1), Cesium.Math.clamp(lastMouseWin.y, 0, h - 1));

    if (endpointExtendMode) {
      updateEndpointExtendPreview(clamped);
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      viewer.scene.requestRender?.();
      return;
    }

    if (draggingEntities && dragStartCartesian) {
      const cartesian = pickCartesian(clamped);
      if (!cartesian) return;
      const offset = Cesium.Cartesian3.subtract(cartesian, dragStartCartesian, new Cesium.Cartesian3());
      dragStartCartesian = cartesian;
      draggingEntities.forEach((entity) => {
        const positions = getPositionsFromEntity(entity);
        if (!positions.length) return;
        const moved = positions.map((pos) => Cesium.Cartesian3.add(pos, offset, new Cesium.Cartesian3()));
        setPositionsToEntity(entity, moved);
      });
      incrementMeasureCurrentBy(offset);
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hideInsertPoint();
      hidePlusIndicator();
      viewer.scene.requestRender?.();
      return;
    }

    if (draggingPoint) {
      const cartesian = pickCartesian(clamped);
      if (!cartesian) {
        hideAllSnapPreviewMarkers();
        hidePlusIndicator();
        return;
      }

      // adaptive rebuild with budget + mouse delta gate
      const nowMs = Date.now();
      const needDelta = !lastMouseForSnap || Math.hypot(clamped.x - lastMouseForSnap.x, clamped.y - lastMouseForSnap.y) >= PERF.MOUSE_MIN_DELTA_PX;

      if (needDelta && (!lastSnapBuildTs || nowMs - lastSnapBuildTs > PERF.SNAP_REBUILD_COOLDOWN_MS)) {
        const tStart = performance.now();
        snapCache = buildSnapCacheGPU(clamped, snapExcludeEntityId, snapExcludeIndex);
        lastSnapBuildTs = nowMs;
        lastMouseForSnap = { x: clamped.x, y: clamped.y };
        // ensure we never blow our frame time; defer visuals if budget exceeded
        if (performance.now() - tStart > PERF.SNAP_REBUILD_BUDGET_MS) {
          // previews will update on next frame
        }
      }

      const fakeMove = { endPosition: clamped };
      let snapKind = null;
      let base = cartesian;

      const vBest = findVertexSnap(fakeMove);
      if (vBest) {
        base = vBest.cartesian;
        snapKind = "vertex";
        hideMidpointSnapIndicator();
      } else {
        const eBest = findEntityMidSnap(fakeMove);
        if (eBest) {
          base = eBest.cartesian;
          snapKind = "entity-mid";
          showMidpointSnapIndicator(eBest.cartesian);
        } else hideMidpointSnapIndicator();
      }

      if (draggingPoint.properties?.isControlPoint) {
        const parentEntityId = readProp(draggingPoint.properties?.parentEntityId);
        const parentEntity = safeGetById(dataSource.entities, parentEntityId);
        if (!parentEntity) return;
        const vertexIndex = readProp(draggingPoint.properties?.pointIndex);
        const obj = controlPointsMap.get(parentEntity.id);
        if (!obj) return;
        const positionsRef = obj.positionsRef;
        const oldPos = positionsRef[vertexIndex];
        let nextPos;
        if (snapKind === "vertex" || snapKind === "entity-mid") {
          // short English comment: snapped position uses target point height
          nextPos = Cesium.Cartesian3.clone(base, new Cesium.Cartesian3());
        } else {
          // short English comment: free move preserves original z
          nextPos = preserveZ(positionsRef[vertexIndex], base);
        }

        positionsRef[vertexIndex] = nextPos;
        draggingPoint.position = nextPos;
        snapIndexMoveVertex(parentEntity, vertexIndex, oldPos, nextPos);
        updateMeasureCurrent(nextPos);
        if (shiftMultiDrag && shiftHeld && linkedVertices?.length && controlPointDragOriginal) {
          applyCartoDeltaToLinkedVertices(controlPointDragOriginal, nextPos);
        }
      } else if (draggingPoint.point) {
        const original = draggingPoint.position.getValue();
        const nextPos = snapKind === "vertex" || snapKind === "entity-mid" ? Cesium.Cartesian3.clone(base, new Cesium.Cartesian3()) : preserveZ(original, base);

        draggingPoint.position = nextPos;
        updateMeasureCurrent(nextPos);
      }
      updateSnapPreviewMarkers(fakeMove);
      hidePlusIndicator();
      viewer.scene.requestRender?.();
    }
  };

  function startAutoPan() {
    if (autoPanActive || !isViewerAlive()) return;
    if (!_origInertia) {
      const s = viewer.scene.screenSpaceCameraController;
      _origInertia = { t: s.inertiaTranslate, z: s.inertiaZoom, r: s.inertiaSpin };
      s.inertiaTranslate = 0.0;
      s.inertiaZoom = Math.min(s.inertiaZoom, 0.1);
      s.inertiaSpin = Math.min(s.inertiaSpin, 0.1);
    }
    autoPanActive = true;
    if (lastMouseWin) edgeAutoPan.setMousePosition(lastMouseWin);
    edgeAutoPan.start();

    lastAutoPanTime = null;
    viewer.scene.postRender.addEventListener(onAutoPanStep);
  }
  function stopAutoPan() {
    if (!autoPanActive) return;
    autoPanActive = false;
    edgeAutoPan.stop();

    autoPanVec = { x: 0, y: 0, magX: 0, magY: 0 };
    lastAutoPanTime = null;
    if (isViewerAlive()) {
      if (_origInertia) {
        const s = viewer.scene.screenSpaceCameraController;
        s.inertiaTranslate = _origInertia.t;
        s.inertiaZoom = _origInertia.z;
        s.inertiaSpin = _origInertia.r;
        _origInertia = null;
      }
      viewer.scene.postRender.removeEventListener(onAutoPanStep);
    }
  }

  // ---------------- Entity delete helpers ----------------
  function deleteEntityWithUndo(entity) {
    if (!entity) return;
    if (isHelperEntity(entity)) return;
    if (editTypeRef.current != "new" && !arcDrawingActive) {
      const origW = originalWidthMap.get(entity.id);
      if (origW != null && entity.polyline) {
        entity.polyline.width = origW;
      }
      const snapshot = cloneEntity(entity);
      pushUndo({ type: "delete", entitySnapshot: snapshot, entityId: entity.id });
    }
    removeControlPointsForEntity(entity);
    originalWidthMap.delete(entity.id);
    dataSource.entities.remove(entity);
    clearCpHighlight();
    if (Array.isArray(selectedEntitiesRef?.current)) {
      selectedEntitiesRef.current = selectedEntitiesRef.current.filter((e) => e.id !== entity.id);
    }
    hideInsertPoint();
    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();
    if (moItem?.arrow_visibility) toggleDirectionalArrows(viewer, dataSource, true, directionArrowSettingRef);
  }

  function deleteVertexFromControlPoint(cpEntity) {
    if (!cpEntity?.properties?.isControlPoint?.getValue?.()) return;
    const parentEntityId = readProp(cpEntity.properties.parentEntityId);
    const vertexIndex = readProp(cpEntity.properties.pointIndex);
    const parentEntity = safeGetById(dataSource.entities, parentEntityId);
    if (!parentEntity) return;
    deleteVertexAtIndex(parentEntity, vertexIndex);
  }

  function deleteVertexAtIndex(parentEntity, vertexIndex) {
    if (!parentEntity || !(parentEntity.polyline || parentEntity.polygon)) return;
    if (editTypeRef.current != "new" && !arcDrawingActive) {
      const origW = originalWidthMap.get(parentEntity.id);
      if (origW != null && parentEntity.polyline) {
        parentEntity.polyline.width = origW;
      }
      const snapshot = cloneEntity(parentEntity);
      pushUndo({ type: "modify", entitySnapshot: snapshot, entityId: parentEntity.id });
    }
    const positions = getPositionsFromEntity(parentEntity);
    if (!Array.isArray(positions) || positions.length === 0) return;
    const idx = Math.max(0, Math.min(vertexIndex, positions.length - 1));
    positions.splice(idx, 1);
    if (parentEntity.polyline && positions.length < 2) {
      clearCpHighlight();
      deleteEntityWithUndo(parentEntity);
      if (focusedControlPointInfo.entityId === parentEntity.id) focusedControlPointInfo = { entityId: null, index: null };
      return;
    }
    if (parentEntity.polygon && positions.length < 3) {
      clearCpHighlight();
      deleteEntityWithUndo(parentEntity);
      if (focusedControlPointInfo.entityId === parentEntity.id) focusedControlPointInfo = { entityId: null, index: null };
      return;
    }
    setPositionsToEntity(parentEntity, positions);
    rebuildControlPointsForEntity(parentEntity, Math.max(0, idx - 1));
    hideInsertPoint();
    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();
  }

  function trySplitEntityAtClick(ent, mouseWin) {
    // only polyline/polygon from this datasource
    if (!(ent && (ent.polyline || ent.polygon))) return;
    if (!dataSource?.entities?.contains?.(ent)) return;

    const positions = getPositionsFromEntity(ent);
    if (!positions || positions.length < 2) return;

    let best = { idx: -1, t: 0.5, dPx: Infinity, wrap: false };
    for (let i = 0; i < positions.length - 1; i++) {
      const a = positions[i];
      const b = positions[i + 1];
      if (Cesium.Cartesian3.distance(a, b) < MIN_SEG_LEN_M) continue;
      const { t, dPx } = projectTOnSegment2D(a, b, mouseWin);
      if (dPx < best.dPx) best = { idx: i, t, dPx, wrap: false };
    }

    if (best.idx < 0 || !isFinite(best.dPx)) return;

    const a = positions[best.idx];
    const b = positions[best.idx + 1];
    const splitPos = interpolateCartographic(a, b, best.t);

    // snapshot for undo
    const origSnapshot = editTypeRef.current != "new" && !arcDrawingActive ? cloneEntity(ent) : null;

    // build two position arrays
    const part1 = positions.slice(0, best.idx + 1);
    part1.push(splitPos);
    const part2 = [splitPos, ...positions.slice(best.idx + 1)];

    // measure lengths to know which one is bigger
    const len1 = polylineLength(part1);
    const len2 = polylineLength(part2);

    // read original prop id
    const originalPropId = readProp(ent.properties?.id);

    // find max properties.id in this datasource for the small part
    let maxPropId = 0;
    const all = dataSource.entities.values;
    for (let i = 0; i < all.length; i++) {
      const pId = readProp(all[i].properties?.id);
      if (typeof pId === "number" && pId > maxPropId) maxPropId = pId;
    }
    const newSmallId = maxPropId + 2;

    // we will keep original entity for bigger part
    const biggerPart = len1 >= len2 ? part1 : part2;
    const smallerPart = len1 >= len2 ? part2 : part1;

    // apply bigger part on original entity
    setPositionsToEntity(ent, biggerPart);

    // clone properties for smaller part
    const smallProps = ent.properties ? new Cesium.PropertyBag(Object.assign({}, ent.properties.getValue?.() || {}, { id: newSmallId })) : new Cesium.PropertyBag({ id: newSmallId });

    // create smaller entity in the SAME datasource
    const smallEntityOpts = {};
    if (ent.polyline) {
      smallEntityOpts.polyline = {
        positions: smallerPart,
        width: ent.polyline.width,
        material: ent.polyline.material,
        clampToGround: ent.polyline.clampToGround,
      };
    } else if (ent.polygon) {
      // optional: handle polygon split if you need
      return;
    }
    smallEntityOpts.properties = smallProps;
    const smallEntity = dataSource.entities.add(smallEntityOpts);

    // rebuild CPs for both
    rebuildControlPointsForEntity(ent);
    rebuildControlPointsForEntity(smallEntity);

    // undo record
    // undo record (single undo)
    if (origSnapshot && typeof pushUndo === "function") {
      pushUndo({
        type: "split",
        dsRef: findDataSourceOfEntity(ent), // use same datasource
        originalEntity: origSnapshot, // entity before split
        newEntities: [smallEntity.id], // the part we just added
      });
    }

    viewer.scene.requestRender?.();
  }

  function polylineLength(positions) {
    let sum = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      sum += Cesium.Cartesian3.distance(positions[i], positions[i + 1]);
    }
    return sum;
  }

  // short English comment: use snapIndex to get nearby entities for highlight
  function getNearbyEntitiesForHighlight(winMouse, ds) {
    if (!SNAP_SYSTEM_ENABLED || !snapIndexReady || !winMouse || !ds) return [];

    const cache = buildSnapCacheGPU(winMouse, null, null);
    if (!cache) return [];

    const seen = new Set();
    const result = [];

    const pushEnt = (ent) => {
      if (!ent) return;
      if (isHelperEntity(ent)) return;
      if (!ds.entities?.contains?.(ent)) return;
      if (!isEntityInteractive(ent, ds)) return;
      if (seen.has(ent.id)) return;
      seen.add(ent.id);
      result.push(ent);
    };

    const verts = cache.vertices || [];
    const mids = cache.entityMids || [];

    for (const v of verts) pushEnt(v.ent);
    for (const m of mids) pushEnt(m.ent);

    return result;
  }

  // ---------------- Helpers ----------------
  function getPolylineWidth(entity) {
    const w = entity?.polyline?.width;
    return typeof w?.getValue === "function" ? w.getValue(Cesium.JulianDate.now()) : w;
  }
  function showEntityHighlightWidth(entityToHighlight) {
    if (entityToHighlight.polyline && !originalWidthMap.has(entityToHighlight.id)) {
      const base = getPolylineWidth(entityToHighlight) ?? 2.0; // read current width
      originalWidthMap.set(entityToHighlight.id, base); // remember original
      entityToHighlight.__originalHighlightWidth = base;
      entityToHighlight.polyline.width = base + 3; // +3 on highlight
    }
  }
  function dropEntityHighlightWidthAll() {
    for (const [id, originalWidth] of Array.from(originalWidthMap.entries())) {
      const e = safeGetById(dataSource.entities, id);
      if (e?.polyline) e.polyline.width = originalWidth;
      if (e) delete e.__originalHighlightWidth;
      originalWidthMap.delete(id);
    }
  }

  function readProp(prop) {
    return typeof prop?.getValue === "function" ? prop.getValue() : prop;
  }

  function applyLaneMarkerVisual(ent) {
    if (!ent?.polyline) return;

    const colorStr = String(readProp(ent.properties?.color) || "").toLowerCase();
    const subtypeStr = String(readProp(ent.properties?.subtype) || "").toLowerCase();

    const cesColor = colorStr.includes("yellow") ? Cesium.Color.YELLOW : Cesium.Color.WHITE;

    if (subtypeStr.includes("dashed")) {
      ent.polyline.material = new Cesium.PolylineDashMaterialProperty({
        color: cesColor,
        dashLength: 16.0,
      });
    } else {
      ent.polyline.material = cesColor;
    }
  }

  // ---------------- Keyboard: Backspace/Delete ----------------
  function resolveFocusFromPointer() {
    if (!lastMouseWin) return false;
    const picked = viewer.scene.pick(lastMouseWin);
    if (picked?.id?.properties?.isControlPoint?.getValue?.()) {
      const parentEntityId = readProp(picked.id.properties.parentEntityId);
      const vertexIndex = readProp(picked.id.properties.pointIndex);
      focusedControlPointInfo = { entityId: parentEntityId, index: vertexIndex };
      highlightCp(parentEntityId, vertexIndex);
      return true;
    }
    return false;
  }
  function performBackspaceDeletion(direction = -1) {
    const { entityId, index } = focusedControlPointInfo || {};
    if (!entityId || index == null) return false;
    const parent = safeGetById(dataSource.entities, entityId);
    if (!parent) {
      focusedControlPointInfo = { entityId: null, index: null };
      clearCpHighlight();
      return false;
    }
    const positions = getPositionsFromEntity(parent);
    if (!positions?.length) {
      clearCpHighlight();
      return false;
    }
    const i = Math.max(0, Math.min(index, positions.length - 1));
    deleteVertexAtIndex(parent, i);
    const still = safeGetById(dataSource.entities, entityId);
    if (!still) {
      focusedControlPointInfo = { entityId: null, index: null };
      clearCpHighlight();
      return false;
    }
    const remained = getPositionsFromEntity(still);
    if (!remained?.length) {
      focusedControlPointInfo = { entityId: null, index: null };
      clearCpHighlight();
      return false;
    }
    if (direction < 0) {
      const nextIdx = Math.max(0, i - 1);
      focusedControlPointInfo = { entityId, index: nextIdx };
      highlightCp(entityId, nextIdx);
    } else {
      const nextIdx = Math.min(i, remained.length - 1);
      focusedControlPointInfo = { entityId, index: nextIdx };
      highlightCp(entityId, nextIdx);
    }
    viewer.scene.requestRender?.();
    return true;
  }
  function stopBackspaceRepeat() {
    if (!backspaceActive) return;
    clearInterval(backspaceTimer);
    backspaceTimer = null;
    backspaceActive = false;
  }
  function backspaceKeyupHandler(evt) {
    if (evt.key === "Backspace") stopBackspaceRepeat();
  }

  keydownHandler = (evt) => {
    // Shift + Arrow => nudge selected nearby vertices (2cm) while Shift held
    if (shiftNudgeActive && shiftHeld) {
      const k = evt.key;
      const isArrow = k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight";
      if (isArrow) {
        evt.preventDefault();
        evt.stopPropagation();

        if (k === "ArrowUp") applyShiftNudge("up");
        if (k === "ArrowDown") applyShiftNudge("down");
        if (k === "ArrowLeft") applyShiftNudge("left");
        if (k === "ArrowRight") applyShiftNudge("right");
        return;
      }
    }

    // Shift + D => delete hovered highlighted entity (no click)
    const isShiftD = evt.shiftKey && (evt.key === "D" || evt.key === "d");
    if (isShiftD) {
      if (draggingPoint || draggingEntities) return;

      const ent = hoveredEntity;
      if (!ent) return;
      if (isHelperEntity(ent)) return;
      if (!dataSource?.entities?.contains?.(ent)) return;

      evt.preventDefault?.();
      evt.stopPropagation?.();

      deleteEntityWithUndo(ent);

      hoveredEntity = null;
      hoveredSegment = -1;
      dropEntityHighlightWidthAll();
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      viewer.scene.requestRender?.();
      return;
    }

    // Lane marker quick style edit (must be hovered)
    {
      const k0 = String(evt.key || "").toLowerCase();
      const isLaneKey = k0 === "w" || k0 === "y" || k0 === "s" || k0 === "d";
      const layerNameLower = String(dataSource?.layerName || "").toLowerCase();
      const isLaneMarkerLayer = layerNameLower.includes("lane_marker");

      if (isLaneKey && isLaneMarkerLayer && hoveredEntity && dataSource?.entities?.contains?.(hoveredEntity) && hoveredEntity?.polyline) {
        evt.preventDefault?.();
        evt.stopPropagation?.();

        if (!hoveredEntity.properties) hoveredEntity.properties = new Cesium.PropertyBag({});

        if (editTypeRef.current != "new" && !arcDrawingActive) {
          const origW = originalWidthMap.get(hoveredEntity.id);
          if (origW != null && hoveredEntity.polyline) hoveredEntity.polyline.width = origW;
          const snapshot = cloneEntity(hoveredEntity);
          pushUndo({ type: "modify", entitySnapshot: snapshot, entityId: hoveredEntity.id });
        }

        if (k0 === "w") hoveredEntity.properties.color = "white";
        if (k0 === "y") hoveredEntity.properties.color = "yellow";
        if (k0 === "s") hoveredEntity.properties.subtype = "solid";
        if (k0 === "d") hoveredEntity.properties.subtype = "dashed";

        applyLaneMarkerVisual(hoveredEntity);
        showEntityHighlightWidth(hoveredEntity);
        viewer.scene.requestRender?.();
        return;
      }
    }

    const isDelete = evt.key === "Delete";
    const isBackspace = evt.key === "Backspace";
    if (!isDelete && !isBackspace) return;
    if (draggingPoint || draggingEntities) return;

    if (isDelete) {
      const picked = viewer.scene.pick(lastMouseWin || new Cesium.Cartesian2(0, 0));
      if (!picked?.id) return;
      if (picked.id?.properties?.isControlPoint?.getValue?.()) {
        deleteVertexFromControlPoint(picked.id);
        return;
      }
      if (!isHelperEntity(picked.id) && dataSource?.entities?.contains?.(picked.id)) deleteEntityWithUndo(picked.id);
      return;
    }

    evt.preventDefault?.();
    if (!(focusedControlPointInfo.entityId && focusedControlPointInfo.index != null)) {
      const ok = resolveFocusFromPointer();
      if (!ok) return;
    }
    const direction = evt.shiftKey ? +1 : -1;
    performBackspaceDeletion(direction);
    if (backspaceActive) return;
    backspaceActive = true;
    backspaceTimer = setInterval(() => {
      const ok = performBackspaceDeletion(direction);
      if (!ok) stopBackspaceRepeat();
    }, 120);
  };

  window.addEventListener("keydown", keydownHandler);
  window.addEventListener("keyup", backspaceKeyupHandler);

  function onShiftKey(e) {
    if (e.type === "keydown" && e.key === "Shift") {
      shiftHeld = true;

      if (!draggingPoint && !draggingEntities) {
        shiftNudgeActive = true;
        attachShiftNudgeCameraChanged();
        refreshMouseAnchorFromLastClient();

        rebuildShiftNudgeSelection(); // immediate
        startShiftNudgePump(800);
      }
    }

    if (e.type === "keyup" && e.key === "Shift") {
      shiftHeld = false;

      shiftNudgeActive = false;
      stopShiftNudgePump();

      detachShiftNudgeCameraChanged();
      clearShiftNudgeSelection();

      if (draggingPoint || draggingEntities) finishDrag("shift-up");
    }
  }

  window.addEventListener("keydown", onShiftKey);
  window.addEventListener("keyup", onShiftKey);

  function onSpaceKey(e) {
    const isSpace = e.code === "Space" || e.key === " " || e.key === "Spacebar";
    if (!isSpace) return;
    spaceHeld = e.type === "keydown";
    if (spaceHeld) {
      e.preventDefault(); // stop scroll
      e.stopPropagation(); // stop Cesium shortcuts
    }
  }
  window.addEventListener("keydown", onSpaceKey);
  window.addEventListener("keyup", onSpaceKey);

  function onGlobalMouseUp() {
    if (draggingPoint || draggingEntities) finishDrag("global-mouseup");
  }
  window.addEventListener("mouseup", onGlobalMouseUp);

  // ---------------- Mouse move (throttled) ----------------
  let lastMoveTs = 0;
  let lastNearScanTs = 0; // last time we ran the near-highlight scan
  let lastNearHighlightEntity = null; // cached entity for near highlight
  const onMouseMove = (movement) => {
    if (!isViewerAlive()) return;

    const zoom = getZoom();

    if (zoom < PERF.ZOOM_MIN_POLYGON) {
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      dropEntityHighlightWidthAll();
      removeAllControlPoints();
      lastNearHighlightEntity = null;
      if (endpointExtendMode) clearDragMeasureOverlay();
      return;
    }

    const lowZoom = zoom < PERF.ZOOM_MIN;
    if (lowZoom) {
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      dropEntityHighlightWidthAll();

      for (const id of Array.from(controlPointsMap.keys())) {
        const e = dataSource.entities.getById(id);
        if (e && !e.polygon) removeControlPointsForEntity(e);
      }

      lastNearHighlightEntity = null;
    }

    const nowTs = performance.now();
    if (nowTs - lastMoveTs < PERF.MOUSE_THROTTLE_MS) return;
    lastMoveTs = nowTs;

    if (arcDrawingActive) {
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      return;
    }

    const prev = lastMouseWin;
    lastMouseWin = movement?.endPosition || lastMouseWin;
    // Shift-nudge must react to mouse move immediately (not only camera change)
    if (shiftNudgeActive && shiftHeld && !draggingPoint && !draggingEntities) {
      scheduleShiftNudgeRebuild(30);
    }

    if (draggingPoint || draggingEntities) {
      // autospan
      updateAutoPanVector(lastMouseWin);

      edgeAutoPan.setMousePosition(lastMouseWin);

      startAutoPan();
    }

    const picked = viewer.scene.pick(movement.endPosition);
    if (picked?.id?.properties?.isInsertPoint?.getValue?.()) {
      if (insertPointEntity) insertPointEntity.show = true;
      return;
    }
    if (endpointExtendMode) {
      updateAutoPanVector(lastMouseWin);
      edgeAutoPan.setMousePosition(lastMouseWin);
      startAutoPan();

      updateEndpointExtendPreview(lastMouseWin);

      // keep other temporary helpers off in this mode
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();

      return;
    }

    if (isDrawingHelper(picked?.id) || editTypeRef.current === "new") {
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      return;
    }

    if (!draggingPoint && picked?.id?.properties?.isControlPoint?.getValue?.()) {
      const parentEntityId = readProp(picked.id.properties.parentEntityId);
      const vertexIndex = readProp(picked.id.properties.pointIndex);
      focusedControlPointInfo = { entityId: parentEntityId, index: vertexIndex };
      highlightCp(parentEntityId, vertexIndex);
    }

    if (draggingEntities && dragStartCartesian) {
      const cartesian = pickCartesian(movement.endPosition);
      if (!cartesian) return;
      const offset = Cesium.Cartesian3.subtract(cartesian, dragStartCartesian, new Cesium.Cartesian3());
      dragStartCartesian = cartesian;
      draggingEntities.forEach((entity) => {
        const positions = getPositionsFromEntity(entity);
        if (!positions.length) return;
        const movedPositions = positions.map((pos) => Cesium.Cartesian3.add(pos, offset, new Cesium.Cartesian3()));
        setPositionsToEntity(entity, movedPositions);
      });
      incrementMeasureCurrentBy(offset);
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hideInsertPoint();
      hidePlusIndicator();
      return;
    }

    if (draggingPoint) {
      const cartesian = pickCartesian(movement.endPosition);
      if (!cartesian) {
        hideAllSnapPreviewMarkers();
        hidePlusIndicator();
        return;
      }

      // only rebuild if mouse moved enough and cadence allows
      const needDelta = !lastMouseForSnap || Math.hypot(lastMouseWin.x - lastMouseForSnap.x, lastMouseWin.y - lastMouseForSnap.y) >= PERF.MOUSE_MIN_DELTA_PX;
      const nowMs = Date.now();
      if (needDelta && (!lastSnapBuildTs || nowMs - lastSnapBuildTs > PERF.SNAP_REBUILD_COOLDOWN_MS)) {
        const tStart = performance.now();
        snapCache = buildSnapCacheGPU(movement.endPosition, snapExcludeEntityId, snapExcludeIndex);
        lastSnapBuildTs = nowMs;
        lastMouseForSnap = { x: lastMouseWin.x, y: lastMouseWin.y };
        if (performance.now() - tStart > PERF.SNAP_REBUILD_BUDGET_MS) {
          // previews will update next frame
        }
      }

      let snapKind = null;
      let base = cartesian;
      const vBest = findVertexSnap(movement);
      if (vBest) {
        base = vBest.cartesian;
        snapKind = "vertex";
        hideMidpointSnapIndicator();
      } else {
        const eBest = findEntityMidSnap(movement);
        if (eBest && isEntityInteractive(eBest.mid?.ent, eBest.mid?.ownerDs)) {
          base = eBest.cartesian;
          snapKind = "entity-mid";
          showMidpointSnapIndicator(eBest.cartesian);
        } else hideMidpointSnapIndicator();
      }
      if (draggingPoint.properties?.isControlPoint) {
        const parentEntity = dataSource.entities.getById(readProp(draggingPoint.properties.parentEntityId));
        const index = readProp(draggingPoint.properties.pointIndex);
        const obj = controlPointsMap.get(parentEntity.id);
        if (!obj) return;
        const positionsRef = obj.positionsRef;
        // replace original computation with same logic
        const oldPos = positionsRef[index];
        let nextPos;
        if (snapKind === "vertex" || snapKind === "entity-mid") {
          nextPos = Cesium.Cartesian3.clone(base, new Cesium.Cartesian3());
        } else {
          nextPos = preserveZ(positionsRef[index], base);
        }

        positionsRef[index] = nextPos;
        draggingPoint.position = nextPos;

        snapIndexMoveVertex(parentEntity, index, oldPos, nextPos);

        updateMeasureCurrent(nextPos);
        // Propagate to linked vertices if Shift-dragging
        if (shiftMultiDrag && shiftHeld && linkedVertices?.length && controlPointDragOriginal) {
          applyCartoDeltaToLinkedVertices(controlPointDragOriginal, nextPos);
        }
      } else if (draggingPoint.point) {
        const original = draggingPoint.position.getValue();
        const nextPos = snapKind === "vertex" || snapKind === "entity-mid" ? Cesium.Cartesian3.clone(base, new Cesium.Cartesian3()) : preserveZ(original, base);

        draggingPoint.position = nextPos;
        updateMeasureCurrent(nextPos);
      }
      updateSnapPreviewMarkers(movement);
      hidePlusIndicator();
      return;
    }

    dropEntityHighlightWidthAll();

    let entityToHighlight = null;
    if ((picked?.id?.polyline || picked?.id?.polygon || picked?.id?.point) && dataSource && dataSource.entities.contains(picked.id)) {
      entityToHighlight = picked.id;
    } else if (picked?.id?.properties?.isControlPoint) {
      const parentId = readProp(picked.id.properties.parentEntityId);
      const parentEntity = safeGetById(dataSource.entities, parentId);
      if (parentEntity) entityToHighlight = parentEntity;
    }

    const winMouse = movement.endPosition;

    if (!entityToHighlight && winMouse && dataSource) {
      const nowMs = Date.now();
      // run near-scan at most every NEAR_SCAN_COOLDOWN_MS
      if (!lastNearScanTs || nowMs - lastNearScanTs >= PERF.NEAR_SCAN_COOLDOWN_MS) {
        lastNearScanTs = nowMs;
        lastNearHighlightEntity = null;

        // short English comment: limit scan using spatial index if available
        const candidates = SNAP_SYSTEM_ENABLED && snapIndexReady ? getNearbyEntitiesForHighlight(winMouse, dataSource) : dataSource.entities.values || [];

        const maxScan = Math.min(candidates.length, PERF.MAX_NEAR_SCAN_ENTITIES);
        for (let i = 0; i < maxScan; i++) {
          const ent = candidates[i];
          if (!(ent?.polyline || ent?.polygon)) continue;
          if (isMouseNearEntityPx(ent, winMouse, PERF.NEAR_HIGHLIGHT_RADIUS_PX)) {
            lastNearHighlightEntity = ent;
            break;
          }
        }
      }

      // reuse cached entity between scans, but recheck distance
      if (lastNearHighlightEntity) {
        if (isMouseNearEntityPx(lastNearHighlightEntity, winMouse, PERF.NEAR_HIGHLIGHT_RADIUS_PX)) {
          entityToHighlight = lastNearHighlightEntity;
        } else {
          lastNearHighlightEntity = null;
        }
      }
    }

    if (!lowZoom && !helpersSuppressed && winMouse && (entityToHighlight?.polyline || entityToHighlight?.polygon)) {
      const positions = getPositionsFromEntity(entityToHighlight);
      const segThreshold = entityToHighlight ? Number.POSITIVE_INFINITY : INSERT_HOVER_THRESHOLD_PX;
      const result = findNearestSegmentPositionPx(positions, winMouse, segThreshold);
      if (result) {
        showInsertPoint(result.position);
        hoveredSegment = result.index;
        hoveredEntity = entityToHighlight;
        lastInsertInfo = { entityId: entityToHighlight.id, seg: result.index, pos: result.position, visible: true };
        showControlPointsForEntity(entityToHighlight, Math.min(result.index + 1, positions.length - 1));
      } else {
        if (lastInsertInfo.visible && lastInsertInfo.entityId === entityToHighlight.id && lastInsertInfo.pos) {
          const dpx = mouseDistPx(winMouse, lastInsertInfo.pos);
          if (dpx <= INSERT_STICKY_LEEWAY_PX) showInsertPoint(lastInsertInfo.pos);
          else if (insertPointEntity) insertPointEntity.show = false;
        } else {
          hideInsertPoint();
        }
        showControlPointsForEntity(entityToHighlight);
      }
    } else {
      hideInsertPoint();
    }

    if (entityToHighlight) {
      showEntityHighlightWidth(entityToHighlight);
      if (!helpersSuppressed && (entityToHighlight.polyline || entityToHighlight.polygon)) showControlPointsForEntity(entityToHighlight);
    } else {
      for (const id of Array.from(controlPointsMap.keys())) {
        const e = dataSource.entities.getById(id);
        if (e) removeControlPointsForEntity(e);
      }
    }

    if (!lowZoom && picked?.id?.properties?.isControlPoint?.getValue?.() && isEndpointControlPoint(picked.id)) {
      showPlusOnControlPoint(picked.id);
    } else {
      hidePlusIndicator();
    }

    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
  };
  handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.SHIFT);
  handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.ALT);
  handler.setInputAction(onMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.CTRL);

  // ---------------- LEFT DOWN ----------------
  // Shared left-down logic. `withShift` comes from KeyboardEventModifier.
  // ---------------- LEFT DOWN ----------------
  // Shared left-down logic. `withShift` comes from KeyboardEventModifier.
  function handleLeftDown(click, withShift, withAlt = false) {
    if (!isViewerAlive()) return;

    if (endpointExtendMode) {
      // short English comment: ignore drag logic in endpoint extend mode
      return;
    }
    lastMouseWin = click?.position || lastMouseWin;
    const picked = viewer.scene.pick(click.position);
    const zoom = getZoom();
    const isCp = !!readProp(picked?.id?.properties?.isControlPoint);

    if (zoom < PERF.ZOOM_MIN) {
      if (!isCp) return;

      const parentEntityId = readProp(picked.id.properties?.parentEntityId);
      const parentEntity = safeGetById(dataSource.entities, parentEntityId);

      if (!parentEntity?.polygon) return;
      if (zoom < PERF.ZOOM_MIN_POLYGON) return;
    }

    if (isDrawingHelper(picked?.id) || editTypeRef.current === "new") return;

    if (spaceHeld && picked?.id && dataSource?.entities?.contains?.(picked.id)) {
      trySplitEntityAtClick(picked.id, click.position);
      return; // stop other left-down logic
    }

    // ---- Start dragging a control point
    if (picked?.id?.properties?.isControlPoint) {
      draggingPoint = picked.id;
      const parentEntityId = readProp(draggingPoint.properties?.parentEntityId);
      const parentEntity = safeGetById(dataSource.entities, parentEntityId);
      if (!parentEntity) return;
      if (!isEntityInteractive(parentEntity, dataSource)) return;

      if (!pendingBulkChanges) pendingBulkChanges = [];
      if (editTypeRef.current != "new" && !arcDrawingActive) {
        const origW = originalWidthMap.get(parentEntity.id);
        if (origW != null && parentEntity.polyline) {
          parentEntity.polyline.width = origW;
        }
        pendingBulkChanges.push({
          dsRef: findDataSourceOfEntity(parentEntity),
          entityId: parentEntity.id,
          entitySnapshot: cloneEntity(parentEntity),
        });
      }

      disableCameraControls(viewer, originalCameraControls);

      const vertexIndex = readProp(draggingPoint.properties?.pointIndex);
      focusedControlPointInfo = { entityId: parentEntity?.id, index: vertexIndex };
      highlightCp(parentEntity?.id, vertexIndex);
      lastDownOnControlPoint = { entityId: parentEntity?.id, index: vertexIndex, ts: Date.now() };

      snapExcludeEntityId = parentEntity?.id;
      snapExcludeIndex = vertexIndex;

      // initial snap cache
      snapCache = buildSnapCacheGPU(click.position, snapExcludeEntityId, snapExcludeIndex);
      lastSnapBuildTs = Date.now();
      lastMouseForSnap = { x: click.position.x, y: click.position.y };

      const obj = controlPointsMap.get(parentEntity?.id);
      controlPointDragOriginal = obj ? Cesium.Cartesian3.clone(obj.positionsRef[vertexIndex], new Cesium.Cartesian3()) : null;

      // --- Shift multi-drag bootstrap (uses 7-d.p. lon/lat equality)
      shiftMultiDrag = !!withShift;
      if (shiftMultiDrag && controlPointDragOriginal) {
        multiDragKey = latLonKeyDeg(controlPointDragOriginal);
        linkedVertices = buildLinkedVerticesForKey(
          multiDragKey,
          parentEntity,
          vertexIndex,
          controlPointDragOriginal, // seed
          PERF.SHIFT_LINK_RADIUS_PX, // 100px
        );

        // short English comment: snapshot parent + linked entities once
        if (editTypeRef.current != "new" && !arcDrawingActive) {
          if (!pendingBulkChanges) pendingBulkChanges = [];
          const seen = new Set(pendingBulkChanges.map((c) => c.entityId));

          if (!seen.has(parentEntity.id)) {
            const origW = originalWidthMap.get(parentEntity.id);
            if (origW != null && parentEntity.polyline) {
              parentEntity.polyline.width = origW;
            }
            pendingBulkChanges.push({
              dsRef: findDataSourceOfEntity(parentEntity),
              entityId: parentEntity.id,
              entitySnapshot: cloneEntity(parentEntity),
            });
            seen.add(parentEntity.id);
          }

          for (const it of linkedVertices) {
            if (seen.has(it.entity.id)) continue;
            const origW = originalWidthMap.get(it.entity.id);
            if (origW != null && it.entity.polyline) {
              it.entity.polyline.width = origW;
            }
            pendingBulkChanges.push({
              dsRef: findDataSourceOfEntity(it.entity),
              entityId: it.entity.id,
              entitySnapshot: cloneEntity(it.entity),
            });
            seen.add(it.entity.id);
          }
        }
      } else {
        linkedVertices = null;
        multiDragKey = null;
      }

      initMeasureForControlPoint(draggingPoint);
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      startAutoPan();
      updateAutoPanVector(lastMouseWin);
      edgeAutoPan.setMousePosition(lastMouseWin);
      return;
    }

    // ---- Insert vertex then start drag
    if (picked?.id?.properties?.isInsertPoint && hoveredEntity) {
      if (!pendingBulkChanges) pendingBulkChanges = [];
      if (editTypeRef.current != "new" && !arcDrawingActive) {
        const origW = originalWidthMap.get(hoveredEntity.id);
        if (origW != null && hoveredEntity.polyline) {
          hoveredEntity.polyline.width = origW;
        }
        pendingBulkChanges.push({
          dsRef: findDataSourceOfEntity(hoveredEntity),
          entityId: hoveredEntity.id,
          entitySnapshot: cloneEntity(hoveredEntity),
        });
      }

      const positions = getPositionsFromEntity(hoveredEntity);
      const insertPosFlat = insertPointEntity.position.getValue();
      const i1 = hoveredSegment,
        i2 = hoveredSegment + 1;
      const p1 = positions[i1],
        p2 = positions[i2];
      const avgH = (Cesium.Cartographic.fromCartesian(p1).height + Cesium.Cartographic.fromCartesian(p2).height) / 2;
      const carto = Cesium.Cartographic.fromCartesian(insertPosFlat);
      const insertPosZPreserved = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, avgH);
      const insertIndex = i2;

      positions.splice(insertIndex, 0, insertPosZPreserved);
      setPositionsToEntity(hoveredEntity, positions);
      rebuildControlPointsForEntity(hoveredEntity, insertIndex);

      const obj = controlPointsMap.get(hoveredEntity.id);
      const cp = obj?.cps?.find((c) => readProp(c.properties.pointIndex) === insertIndex);
      if (cp) {
        draggingPoint = cp;
        focusedControlPointInfo = { entityId: hoveredEntity.id, index: insertIndex };
        highlightCp(hoveredEntity.id, insertIndex);
        controlPointDragOriginal = Cesium.Cartesian3.clone(insertPosZPreserved, new Cesium.Cartesian3());

        // Shift multi-drag bootstrap for inserted vertex
        shiftMultiDrag = !!withShift;
        if (shiftMultiDrag && controlPointDragOriginal) {
          multiDragKey = latLonKeyDeg(controlPointDragOriginal);
          linkedVertices = buildLinkedVerticesForKey(
            multiDragKey,
            hoveredEntity,
            insertIndex,
            controlPointDragOriginal, // seed
            PERF.SHIFT_LINK_RADIUS_PX, // 100px
          );

          if (editTypeRef.current != "new" && !arcDrawingActive) {
            if (!pendingBulkChanges) pendingBulkChanges = [];
            const seen = new Set(pendingBulkChanges.map((c) => c.entityId));

            // short English comment: ensure hovered entity snapshot is stored once
            if (!seen.has(hoveredEntity.id)) {
              const origW = originalWidthMap.get(hoveredEntity.id);
              if (origW != null && hoveredEntity.polyline) {
                hoveredEntity.polyline.width = origW;
              }
              pendingBulkChanges.push({
                dsRef: findDataSourceOfEntity(hoveredEntity),
                entityId: hoveredEntity.id,
                entitySnapshot: cloneEntity(hoveredEntity),
              });
              seen.add(hoveredEntity.id);
            }

            // short English comment: also snapshot all linked entities once
            for (const it of linkedVertices) {
              if (seen.has(it.entity.id)) continue;
              const origW = originalWidthMap.get(it.entity.id);
              if (origW != null && it.entity.polyline) {
                it.entity.polyline.width = origW;
              }
              pendingBulkChanges.push({
                dsRef: findDataSourceOfEntity(it.entity),
                entityId: it.entity.id,
                entitySnapshot: cloneEntity(it.entity),
              });
              seen.add(it.entity.id);
            }
          }
        } else {
          linkedVertices = null;
          multiDragKey = null;
        }

        snapExcludeEntityId = hoveredEntity.id;
        snapExcludeIndex = insertIndex;

        snapCache = buildSnapCacheGPU(click.position, snapExcludeEntityId, snapExcludeIndex);
        lastSnapBuildTs = Date.now();
        lastMouseForSnap = { x: click.position.x, y: click.position.y };

        disableCameraControls(viewer, originalCameraControls);
        initMeasureForControlPoint(cp);
        const pick = pickCartesian(click.position);
        if (pick) {
          const nextPos = preserveZ(insertPosZPreserved, pick);
          obj.positionsRef[insertIndex] = nextPos;
          cp.position = nextPos;
          updateMeasureCurrent(nextPos);
        }
        startAutoPan();
        updateAutoPanVector(lastMouseWin);
        edgeAutoPan.setMousePosition(lastMouseWin);
      }
      lastInsertInfo = { entityId: null, seg: null, pos: null, visible: false };
      hideInsertPoint();
      hidePlusIndicator();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      viewer.scene.requestRender?.();
      return;
    }

    // ---- Group drag (Space or Alt) remains unchanged ...
    const inThisDS = (ent) => !!ent && dataSource?.entities?.contains?.(ent) && !isHelperEntity(ent) && isEntityInteractive(ent, dataSource);

    if (withAlt && picked?.id) {
      const base = (selectedEntitiesRef.current?.length > 0 ? selectedEntitiesRef.current : [picked.id]) || [];
      const entitiesToDrag = base.filter(inThisDS);

      if (entitiesToDrag.length === 0) return; // nothing valid to drag

      entitiesToDrag.forEach((ent) => {
        // short English comment: snapshot before group drag
        if (editTypeRef.current != "new" && !arcDrawingActive) {
          const origW = originalWidthMap.get(ent.id);
          if (origW != null && ent.polyline) {
            ent.polyline.width = origW;
          }
          const snapshot = cloneEntity(ent);
          pushUndo({ type: "modify", entitySnapshot: snapshot, entityId: ent.id });
        }
      });

      const startPick = pickCartesian(click.position); // short English comment: drag start on ground

      draggingEntities = entitiesToDrag;
      dragStartCartesian = startPick; // same reference for move offset
      disableCameraControls(viewer, originalCameraControls);
      initMeasureForEntities(draggingEntities, startPick);
      helpersSuppressed = true;
      hideInsertPoint();
      removeAllControlPoints();
      snapCache = null;
      snapExcludeEntityId = null;
      snapExcludeIndex = null;
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();
      focusedControlPointInfo = { entityId: null, index: null };
      startAutoPan();
      updateAutoPanVector(lastMouseWin);
      edgeAutoPan.setMousePosition(lastMouseWin);
    }
  }

  // Register both handlers: default and Shift-modified
  handler.setInputAction((c) => handleLeftDown(c, false, false), Cesium.ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction((c) => handleLeftDown(c, false, true), Cesium.ScreenSpaceEventType.LEFT_DOWN, Cesium.KeyboardEventModifier.ALT);
  handler.setInputAction((c) => handleLeftDown(c, true, false), Cesium.ScreenSpaceEventType.LEFT_DOWN, Cesium.KeyboardEventModifier.SHIFT);

  // ---------------- LEFT UP ----------------
  handler.setInputAction(() => {
    if (!isViewerAlive()) return;
    finishDrag("left-up");
  }, Cesium.ScreenSpaceEventType.LEFT_UP);
  handler.setInputAction(
    () => {
      if (!isViewerAlive()) return;
      finishDrag("left-up-shift");
    },
    Cesium.ScreenSpaceEventType.LEFT_UP,
    Cesium.KeyboardEventModifier.SHIFT,
  );
  handler.setInputAction(
    () => {
      if (!isViewerAlive()) return;
      finishDrag("left-up-alt");
    },
    Cesium.ScreenSpaceEventType.LEFT_UP,
    Cesium.KeyboardEventModifier.ALT,
  );
  handler.setInputAction(
    () => {
      if (!isViewerAlive()) return;
      finishDrag("left-up-ctrl");
    },
    Cesium.ScreenSpaceEventType.LEFT_UP,
    Cesium.KeyboardEventModifier.CTRL,
  );

  // ---------------- LEFT CLICK (endpoint "+") ----------------
  // SHIFT + LEFT_CLICK: insert a vertex at the clicked location on the picked entity without starting a drag
  handler.setInputAction(
    (click) => {
      if (!isViewerAlive() || !canShowDetail()) return;
      if (arcDrawingActive || editTypeRef.current === "new") return;

      const picked = viewer.scene.pick(click.position);
      const ent = picked?.id;
      if (!ent) return;

      // Ignore helpers and control points
      if (ent?.properties?.isControlPoint?.getValue?.() || isHelperEntity(ent)) return;

      // Only polyline/polygon in this dataSource
      if (!(dataSource?.entities?.contains?.(ent) && (ent.polyline || ent.polygon))) return;

      const positions = getPositionsFromEntity(ent);
      if (!positions || positions.length < 2) return;

      const mouseWin = click.position;

      // Find nearest segment in screen-space and its param t
      let best = { idx: -1, t: 0.5, dPx: Infinity, wrap: false };
      for (let i = 0; i < positions.length - 1; i++) {
        const a = positions[i],
          b = positions[i + 1];
        if (Cesium.Cartesian3.distance(a, b) < MIN_SEG_LEN_M) continue;
        const { t, dPx } = projectTOnSegment2D(a, b, mouseWin);
        if (dPx < best.dPx) best = { idx: i, t, dPx, wrap: false };
      }
      // Include closing edge for polygons
      if (ent.polygon && positions.length >= 3) {
        const a = positions[positions.length - 1],
          b = positions[0];
        if (Cesium.Cartesian3.distance(a, b) >= MIN_SEG_LEN_M) {
          const { t, dPx } = projectTOnSegment2D(a, b, mouseWin);
          if (dPx < best.dPx) best = { idx: positions.length - 1, t, dPx, wrap: true };
        }
      }
      if (best.idx < 0 || !isFinite(best.dPx)) return;

      // Compute 3D point on the chosen segment
      const a = positions[best.idx];
      const b = best.wrap ? positions[0] : positions[best.idx + 1];
      const insertPos = interpolateCartographic(a, b, best.t);

      // Undo snapshot (no drag, single modify)
      if (editTypeRef.current != "new" && !arcDrawingActive) {
        const origW = originalWidthMap.get(ent.id);
        if (origW != null && ent.polyline) ent.polyline.width = origW;
        const snapshot = cloneEntity(ent);
        pushUndo({ type: "modify", entitySnapshot: snapshot, entityId: ent.id });
      }

      // Insert at exact segment location
      const insertIndex = best.wrap ? positions.length : best.idx + 1;
      positions.splice(insertIndex, 0, insertPos);
      setPositionsToEntity(ent, positions);
      rebuildControlPointsForEntity(ent, insertIndex);

      showEntityHighlightWidth(ent); // keep widened width highlight
      focusedControlPointInfo = { entityId: ent.id, index: insertIndex }; // set focus
      showControlPointsForEntity(ent, insertIndex); // ensure CPs are visible around hotspot
      highlightCp(ent.id, insertIndex); // white ring on the new vertex
      viewer.scene.requestRender?.();

      // Clean helpers, no drag starts
      hideInsertPoint();
      hideMidpointSnapIndicator();
      hideAllSnapPreviewMarkers();
      hidePlusIndicator();

      if (typeof showToast === "function") {
        showToast("Added a point", Type.NOTIFICATION.SUCCESS);
      }
      viewer.scene.requestRender?.();
    },
    Cesium.ScreenSpaceEventType.LEFT_CLICK,
    Cesium.KeyboardEventModifier.SHIFT,
  );

  handler.setInputAction((click) => {
    if (!isViewerAlive()) return;

    // short English comment: if mode is active, use click to add a point
    if (endpointExtendMode) {
      handleEndpointExtendClick(click);
      return;
    }

    const picked = viewer.scene.pick(click.position);
    const cp = picked?.id;
    if (!cp?.properties?.isControlPoint?.getValue?.()) return;
    if (!isEndpointControlPoint(cp)) return;
    if (isDrawingHelper(cp) || editTypeRef.current === "new") return;

    const parentEntityId = readProp(cp.properties.parentEntityId);
    const parentEntity = safeGetById(dataSource.entities, parentEntityId);
    if (!parentEntity?.polyline) return;

    const vertexIndex = readProp(cp.properties.pointIndex);
    const positions = getPositionsFromEntity(parentEntity);
    if (!positions?.length) return;

    const lastIdx = positions.length - 1;
    const atHead = vertexIndex === 0;
    if (!(atHead || vertexIndex === lastIdx)) return;

    // short English comment: enter endpoint extend mode
    endpointExtendMode = {
      parentEntityId,
      atHead,
      undoCaptured: false,
    };
    clearDragMeasureOverlay();
    dragMeasureMode = "mid";
    dragMeasureCpEntity = null;

    lastMouseWin = lastMouseWin || click.position;
    disableCameraControls(viewer, originalCameraControls);
    updateAutoPanVector(lastMouseWin);
    edgeAutoPan.setMousePosition(lastMouseWin);
    startAutoPan();

    // show initial preview (if mouse pos exists, otherwise uses click pos)
    updateEndpointExtendPreview(lastMouseWin);

    hideInsertPoint();
    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();

    if (typeof showToast === "function") {
      showToast("Add mode: left-click to add points, right-click to finish", Type.NOTIFICATION.INFO);
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction((click) => {
    if (!isViewerAlive()) return;
    if (!endpointExtendMode) return;

    // short English comment: exit endpoint extend mode on right click
    endpointExtendMode = null;
    clearDragMeasureOverlay();
    hideInsertPoint();
    hideMidpointSnapIndicator();
    hideAllSnapPreviewMarkers();
    hidePlusIndicator();
    showToast("Add mode finished", Type.NOTIFICATION.INFO);
    viewer.scene.requestRender?.();
  }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  // Put near top of file
  function reorderDataSourcesByPriority(viewer) {
    const coll = viewer?.dataSources;
    if (!coll) return;
    // stable copy
    const list = coll._dataSources.slice().map((ds, i) => ({
      ds,
      p: Number.isFinite(ds?._priority) ? ds._priority : 0,
      t: Number.isFinite(ds?._priorityOrder) ? ds._priorityOrder : i, // tie-breaker
    }));
    // low -> high, then raiseToTop => highest ends up on top
    list.sort((a, b) => a.p - b.p || a.t - b.t);
    for (const { ds } of list) coll.raiseToTop(ds);
  }

  // ---------------- Cleanup / destroy ----------------
  const _destroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    clearCpHighlight();
    stopAutoPan();
    try {
      edgeAutoPan.destroy();
    } catch {}
    stopBackspaceRepeat();
    if (keydownHandler) {
      window.removeEventListener("keydown", keydownHandler);
      keydownHandler = null;
    }
    window.removeEventListener("keyup", backspaceKeyupHandler);

    window.removeEventListener("keydown", onShiftKey);
    window.removeEventListener("keyup", onShiftKey);
    window.removeEventListener("mouseup", onGlobalMouseUp);

    window.removeEventListener("keydown", onSpaceKey);
    window.removeEventListener("keyup", onSpaceKey);

    const once = () => {
      viewer.scene.postRender.removeEventListener(once);
      try {
        if (isViewerAlive()) {
          viewer.dataSources.remove(overlayDs, true);
          reorderDataSourcesByPriority(viewer);
        }
      } catch {}
    };
    viewer.scene.postRender.addEventListener(once);
    window.removeEventListener("mousemove", onRawMouseMove, RAW_MOUSE_OPTS);
    window.removeEventListener("pointermove", onRawMouseMove, RAW_MOUSE_OPTS);
    window.removeEventListener("mousedown", onRawMouseMove, RAW_MOUSE_OPTS);
    window.removeEventListener("pointerdown", onRawMouseMove, RAW_MOUSE_OPTS);
    window.removeEventListener("wheel", onRawMouseMove, RAW_MOUSE_OPTS);

    _destroy();
  };

  return handler;
};
